import React, { useEffect, useState, useCallback, useRef } from "react";
import { BuildIdentity } from "@/components/BuildIdentity";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MediaLightbox } from "@/components/MediaLightbox";
import { TagInput } from "@/components/TagInput";
import { SmartCover } from "@/components/SmartCover";
import { GallerySourcePanel } from "@/components/GallerySourcePanel";
import { TagIntegrityPanel } from "@/components/TagIntegrityPanel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, fileUrl, thumbUrl, videoUrl, posterUrl, resolveAvatar, displayTitle } from "@/lib/api";
import { uploadMediaFile, describeUploadError, newTraceId } from "@/lib/upload";
import { preflightVideo, PREFLIGHT_BUCKETS } from "@/lib/videoPreflight";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Check, X, Loader2, Image as ImageIcon, Video, Users, Star, Layers, Clock, Shield, CheckSquare,
  LayoutDashboard, FolderOpen, UploadCloud, Trash2, Search, Ban, RotateCcw, Eye, Sparkles,
  Settings as SettingsIcon, Plus, Flag, ArrowUp, ArrowDown, RefreshCw, Tag, Save, Pencil,
  Bell, MessageSquare, TrendingUp, TrendingDown, CreditCard, Send, Home, ChevronDown, ChevronRight, LogOut, GripVertical, MapPin, Inbox, Play,
  AlertTriangle, CheckCircle2, ArrowRight, Copy, FileWarning, Film,
  BookOpen, BookmarkPlus,
} from "lucide-react";
import { Avatar as A2, AvatarFallback as A2F, AvatarImage as A2I } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "inbox", label: "Inbox", icon: Inbox, nav: true },
  { id: "content", label: "Content", icon: FolderOpen },
  { id: "galleries", label: "Galleries", icon: Layers },
  { id: "uploads", label: "Uploads & Processing", icon: Clock },
  { id: "upload", label: "Add Content", icon: UploadCloud },
  { id: "members", label: "Users", icon: Users },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "ranking", label: "Ratings & Rankings", icon: Star },
  { id: "tags", label: "Tags & Search", icon: Sparkles },
  { id: "tag-integrity", label: "Tag Integrity Review", icon: Shield },
  { id: "caption-studio", label: "AI Caption Studio", icon: Sparkles },
  { id: "reports", label: "Moderation", icon: Shield },
  { id: "homepage", label: "Homepage Control", icon: LayoutDashboard },
  { id: "banners", label: "Banners / Featured", icon: ImageIcon },
  { id: "comments", label: "Comments", icon: Layers },
  { id: "sessions", label: "To Review", icon: CheckSquare },
  { id: "trash", label: "Trash", icon: Trash2 },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : n);
const Delta = ({ v, invert }) => {
  if (v === undefined || v === null) return null;
  if (v === 0) return <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#8b8b8b]">±0%</span>;
  const good = invert ? v <= 0 : v >= 0;
  const Icon = v >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={cn("inline-flex items-center gap-1 text-[11px] font-medium", good ? "text-[#10b981]" : "text-[#ef4444]")}>
      <Icon className="w-3 h-3" />{v >= 0 ? "+" : "−"}{Math.abs(v)}%
    </span>
  );
};

const Sparkline = ({ data = [] }) => {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const pts = data.map((d, i) => `${(i / (data.length - 1)) * 100},${28 - (d / max) * 24 - 2}`).join(" ");
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="w-full h-8" aria-hidden="true" focusable="false">
      <polyline points={pts} fill="none" stroke="#d9a24a" strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const Admin = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_ALIASES = { users: "members", inbox: "inbox", uploads: "uploads", captions: "caption-studio", "caption": "caption-studio", tags: "tags", gallery: "galleries", smart: "galleries" };
  const validTab = (t) => TABS.some((x) => x.id === t && !x.nav) ? t : (TAB_ALIASES[t] || "overview");
  const [tab, setTabState] = useState(() => validTab(searchParams.get("tab")));
  const setTab = useCallback((t) => { setTabState(t); setSearchParams(t === "overview" ? {} : { tab: t }, { replace: true }); }, [setSearchParams]);
  const editSmartId = searchParams.get("edit");
  const attention = searchParams.get("attention");
  const gallerySub = ["manager", "smart", "organizer"].includes(searchParams.get("sub")) ? searchParams.get("sub") : "manager";
  const setGallerySub = useCallback((s) => setSearchParams(s === "manager" ? { tab: "galleries" } : { tab: "galleries", sub: s }, { replace: true }), [setSearchParams]);
  const captionSub = ["overview", "training", "testing"].includes(searchParams.get("sub")) ? searchParams.get("sub") : "overview";
  const setCaptionSub = useCallback((s) => setSearchParams(s === "overview" ? { tab: "caption-studio" } : { tab: "caption-studio", sub: s }, { replace: true }), [setSearchParams]);
  useEffect(() => {
    const raw = searchParams.get("tab"); const t = validTab(raw);
    if (raw && raw !== t) {   // unknown / alias tab id -> canonical URL (deep links never silently dead-end on the dashboard)
      if (!TABS.some((x) => x.id === raw)) toast.message(TAB_ALIASES[raw] ? `Opened ${t}` : `Unknown admin section "${raw}" — showing the dashboard`);
      setSearchParams({ ...Object.fromEntries(searchParams), tab: t }, { replace: true }); return;
    }
    setTabState((cur) => (cur === t ? cur : t));
  }, [searchParams]); // eslint-disable-line
  // Legacy deep link: Smart Galleries moved from Tags & Search to Galleries → Smart Galleries
  useEffect(() => {
    if (searchParams.get("tab") === "tags" && searchParams.get("edit")) setSearchParams({ tab: "galleries", sub: "smart", edit: searchParams.get("edit") }, { replace: true });
  }, [searchParams, setSearchParams]);
  const gotoAttention = useCallback((kind) => setSearchParams({ tab: "content", attention: kind }), [setSearchParams]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const openMedia = useCallback((id, list) => { setSelected(id); setPlaylist(Array.isArray(list) ? list : null); }, []);
  const [q, setQ] = useState("");
  const [mobileNav, setMobileNav] = useState(false);
  const [inboxId, setInboxId] = useState(null);
  const [integrityKey, setIntegrityKey] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_admin) { toast.error("Owners only"); navigate("/"); return; }
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/collections/system").then((r) => { const ib = (r.data || []).find((c) => c.system_key === "inbox"); if (ib) setInboxId(ib.id); }).catch(() => {});
  }, [user, authLoading, navigate]);

  if (authLoading || !user?.is_admin) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const refreshStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  const submitSearch = (e) => { e.preventDefault(); if (q.trim()) setTab("content"); };

  const NavList = () => (
    <>
      {TABS.map((t) => {
        const activeTab = tab === t.id;
        const badge = t.id === "uploads" ? stats?.pending : t.id === "reports" ? stats?.reported : t.id === "messages" ? stats?.unread_messages : t.id === "comments" ? stats?.comments : t.id === "inbox" ? stats?.inbox : 0;
        const onClick = t.nav
          ? () => { setMobileNav(false); navigate("/inbox"); }
          : () => { setTab(t.id); setMobileNav(false); };
        return (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={onClick}
            className={cn("flex items-center gap-3 px-4 py-2 mx-3 rounded-md text-sm transition-colors",
              activeTab ? "text-[#d9a24a] bg-[#d9a24a]/10 font-medium" : "text-[#8b8b8b] hover:text-white hover:bg-[#262626]/50")}>
            <t.icon className="w-4 h-4 shrink-0" /> <span className="truncate">{t.label}</span>
            {badge > 0 && <span className="ml-auto text-[10px] font-bold bg-[#d9a24a]/20 text-[#d9a24a] rounded-full px-1.5 py-0.5">{badge}</span>}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white relative z-10">
      {/* Sidebar */}
      <aside className={cn("fixed inset-y-0 left-0 w-[240px] bg-[#0a0a0a] border-r border-[#262626] flex flex-col z-40 transition-transform lg:translate-x-0",
        mobileNav ? "translate-x-0" : "-translate-x-full")}>
        <button data-testid="admin-logo" onClick={() => navigate("/")} className="flex items-center gap-2.5 px-5 h-16 border-b border-[#262626] shrink-0">
          <img src="/logo-icon.png" alt="" className="h-7 w-auto" />
          <span className="font-heading text-xl font-semibold text-[#d9a24a]">BareFootage</span>
        </button>
        <nav className="flex-1 overflow-y-auto py-3 space-y-0.5" data-testid="admin-sidebar-nav">
          <NavList />
        </nav>
      </aside>
      {mobileNav && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setMobileNav(false)} />}

      {/* Topbar */}
      <header className="h-16 lg:ml-[240px] px-4 sm:px-8 flex items-center justify-between border-b border-[#262626] sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-xl z-30">
        <button className="lg:hidden text-[#8b8b8b]" onClick={() => setMobileNav(true)} data-testid="admin-menu"><Layers className="w-5 h-5" /></button>
        <form onSubmit={submitSearch} className="relative w-full max-w-md hidden sm:flex items-center bg-[#141414] border border-[#262626] rounded-md px-3 py-1.5 focus-within:border-[#d9a24a] transition-colors">
          <Search className="w-4 h-4 text-[#8b8b8b] shrink-0" />
          <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search photos, videos, users, tags…" className="bg-transparent flex-1 px-2 text-sm outline-none placeholder:text-[#8b8b8b]" />
          <span className="text-[10px] text-[#8b8b8b] border border-[#262626] rounded px-1">⌘K</span>
        </form>
        <div className="flex items-center gap-3">
          {/* Site Overview — relocated from the sidebar bottom-left cards into the toolbar */}
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="admin-site-overview" title="Site overview" className="inline-flex items-center gap-2 text-xs text-[#8b8b8b] hover:text-white border border-[#262626] rounded-md px-2.5 py-1.5 transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" /><span className="font-mono text-[#10b981]" data-testid="overview-online">{stats?.online_now ?? 0}</span><span className="hidden md:inline">online</span>
                <span className="hidden md:inline text-[#8b8b8b]/60">·</span><span className="hidden md:inline font-mono">{fmt(stats?.media_files ?? 0)}</span><span className="hidden md:inline">files</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 bg-[#141414] border-[#262626] text-white p-4 space-y-3" data-testid="admin-site-overview-panel">
              <div className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">Site Overview</div>
              <Sparkline data={stats?.signups_series} />
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between"><span className="text-[#8b8b8b]">Online now</span><span className="font-mono text-[#10b981]">{stats?.online_now ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-[#8b8b8b]">New signups (7d)</span><span className="font-mono">{stats?.signups_7d ?? 0}</span></div>
                <div className="flex justify-between"><span className="text-[#8b8b8b]">Media files</span><span className="font-mono">{fmt(stats?.media_files ?? 0)}</span></div>
              </div>
            </PopoverContent>
          </Popover>
          <button data-testid="admin-bell" onClick={() => setTab("messages")} className="relative h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-[#262626] text-[#8b8b8b] hover:text-white transition-colors">
            <Bell className="w-4 h-4" />
            {stats?.unread_messages > 0 && <span className="absolute top-1 right-1 text-[9px] font-bold bg-[#d9a24a] text-black rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">{stats.unread_messages}</span>}
          </button>
          <button onClick={() => navigate("/")} className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[#8b8b8b] hover:text-white border border-[#262626] rounded-md px-3 py-1.5 transition-colors"><Home className="w-3.5 h-3.5" />View site</button>
          {/* Account menu — "Signed in as owner" + Sign out relocated here from the sidebar */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="admin-account-menu" className="flex items-center gap-2 bg-[#141414] border border-[#262626] rounded-md pl-1 pr-2 py-1 hover:border-[#d9a24a]/50 transition-colors">
                <A2 className="w-7 h-7"><A2I src={resolveAvatar(user.avatar_url) || user.picture} /><A2F className="bg-secondary text-[10px]">{(user.display_name || user.name)?.[0]}</A2F></A2>
                <span className="text-sm hidden sm:inline">Admin</span><ChevronDown className="w-3.5 h-3.5 text-[#8b8b8b]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#141414] border-[#262626] text-white">
              <DropdownMenuLabel className="font-normal">
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b]">Signed in as owner</div>
                <div className="text-sm font-medium truncate" data-testid="admin-account-name">{user.display_name || user.name}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#262626]" />
              <DropdownMenuItem onClick={() => navigate("/")} className="cursor-pointer"><Home className="w-3.5 h-3.5 mr-2" /> View site</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer"><Users className="w-3.5 h-3.5 mr-2" /> My profile</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#262626]" />
              <DropdownMenuItem data-testid="admin-logout" onClick={logout} className="cursor-pointer text-red-300 focus:text-red-200"><LogOut className="w-3.5 h-3.5 mr-2" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main */}
      <main className="lg:ml-[240px] p-4 sm:p-8 min-h-screen">
        {tab === "overview" && <Dashboard stats={stats} onGoto={setTab} onAttention={gotoAttention} onOpen={openMedia} refreshStats={refreshStats} onInbox={() => { if (inboxId) navigate(`/collection/${inboxId}`); else toast("Inbox is empty right now"); }} />}
        {tab !== "overview" && (
          <div className="max-w-[1400px]">
            <h1 className="font-heading text-2xl font-medium tracking-tight mb-6">{TABS.find((t) => t.id === tab)?.label}</h1>
            {tab === "content" && <ContentLibrary key={attention || "all"} onOpen={openMedia} onChange={refreshStats} initialQ={q} attention={attention} onClearAttention={() => setSearchParams({ tab: "content" })} showInboxTools />}
            {tab === "uploads" && <UploadsProcessing onOpen={openMedia} onChange={refreshStats} />}
            {tab === "upload" && <BulkUpload onDone={() => { refreshStats(); setTab("content"); }} />}
            {tab === "members" && <Members onChange={refreshStats} onOpenProfile={(id) => navigate(`/u/${id}`)} />}
            {tab === "messages" && <MessagesPanel onChange={refreshStats} />}
            {tab === "galleries" && <GalleriesPanel sub={gallerySub} setSub={setGallerySub} initialEditId={editSmartId} onEditConsumed={() => setSearchParams({ tab: "galleries", sub: "smart" }, { replace: true })} />}
            {tab === "ranking" && <RankingPanel onChange={refreshStats} />}
            {tab === "tags" && <TagsPanel />}
            {tab === "tag-integrity" && <TagIntegrityPanel onOpen={openMedia} refreshKey={integrityKey} />}
            {tab === "caption-studio" && <CaptionStudioPanel sub={captionSub} setSub={setCaptionSub} />}
            {tab === "reports" && <ReportsPanel onOpen={setSelected} onChange={refreshStats} />}
            {tab === "homepage" && <HomepagePanel />}
            {tab === "banners" && <BannersPanel />}
            {tab === "comments" && <CommentsPanel onChange={refreshStats} />}
            {tab === "sessions" && <SessionsPanel onOpen={openMedia} />}
            {tab === "trash" && <TrashPanel onChange={refreshStats} />}
            {tab === "settings" && (<><BuildIdentity /><SettingsPanel /></>)}
          </div>
        )}
      </main>
      <MediaLightbox mediaId={selected} open={!!selected} onClose={() => { setSelected(null); setPlaylist(null); if (tab === "tag-integrity") setIntegrityKey((k) => k + 1); }} onChanged={refreshStats} onNavigate={setSelected} playlist={playlist} />
    </div>
  );
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

// Compact caption status + shortcut for the command center (full studio lives in the AI Caption Studio tab)
const CaptionShortcut = ({ onOpen }) => {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/ai/caption-status").then((r) => setSt(r.data)).catch(() => {}); }, []);
  const dot = { healthy: "bg-emerald-400", warning: "bg-amber-400", error: "bg-red-500" }[st?.health] || "bg-[#8b8b8b]";
  return (
    <div className="mt-3 rounded-lg border border-[#262626] bg-[#141414] px-3 py-2.5 flex items-center gap-3 min-w-0" data-testid="dash-caption-shortcut">
      <Sparkles className="w-4 h-4 text-[#d9a24a] shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">AI Captions</div>
        <div className="text-xs text-[#c9c9c9] truncate" data-testid="dash-caption-models">{st?.stage1_model || "gpt-5.4"} → {st?.stage2_model || "gpt-5.4-mini"}</div>
        <div className="text-[11px] text-[#8b8b8b] truncate inline-flex items-center gap-1.5" data-testid="dash-caption-summary">
          <span className={cn("w-1.5 h-1.5 rounded-full", dot)} aria-hidden="true" /> {st ? `${st.approved_examples} approved example${st.approved_examples === 1 ? "" : "s"} · ${st.health.charAt(0).toUpperCase() + st.health.slice(1)}` : "Loading…"}
        </div>
      </div>
      <button type="button" data-testid="dash-open-caption-studio" onClick={onOpen} className="text-xs text-[#d9a24a] hover:underline shrink-0">Open Caption Studio</button>
    </div>
  );
};

const Dashboard = ({ stats, onGoto, onAttention, onOpen, refreshStats, onInbox }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/dashboard"); setData(r.data); } catch { /* keep last */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const s = data?.stats || {};
  const libs = data?.libraries || {};
  const needs = data?.needs || {};
  const recent = (data?.recent || []).slice(0, 8);

  const statCards = [
    { label: "Total Media", value: s.total ?? 0, icon: Layers },
    { label: "Photos", value: s.photos ?? 0, icon: ImageIcon },
    { label: "Videos", value: s.videos ?? 0, icon: Video },
    { label: "Community", value: s.community ?? 0, icon: Users },
    { label: "Pending Review", value: s.pending ?? 0, icon: Clock, hot: (s.pending ?? 0) > 0 },
  ];
  const libRow = [["originals", "Originals"], ["celebrities", "Celebrities"], ["pornstars", "Pornstars"], ["web_finds", "Best of the Web"], ["community", "Community"]];

  // Only surface items that actually need action — each with ONE direct action.
  const attention = [
    { key: "pending_community", n: needs.pending_community, icon: Inbox, label: (n) => `${n} community submission${n > 1 ? "s" : ""} awaiting review`, cta: "Review", go: () => onGoto("uploads") },
    { key: "videos_failed", n: needs.videos_failed, icon: FileWarning, label: (n) => `${n} video${n > 1 ? "s" : ""} failed processing`, cta: "Manage Processing", go: () => onGoto("uploads") },
    { key: "videos_processing", n: needs.videos_processing, icon: Loader2, spin: true, label: (n) => `${n} video${n > 1 ? "s" : ""} still processing`, cta: "Open", go: () => onGoto("uploads") },
    { key: "videos_need_processing", n: needs.videos_need_processing, icon: Film, label: (n) => `${n} video${n > 1 ? "s" : ""} need processing`, cta: "Manage Processing", go: () => onGoto("uploads") },
    { key: "tag_integrity", n: needs.tag_integrity, icon: Tag, label: (n) => `${n} item${n > 1 ? "s" : ""} with a tag verification mismatch`, cta: "Open", go: () => onAttention("tag_integrity") },
    { key: "ai_failed", n: needs.ai_failed, icon: Sparkles, label: (n) => `${n} incomplete AI job${n > 1 ? "s" : ""} (recorded attempt failed or stalled)`, cta: "Open", go: () => onAttention("ai_incomplete") },
    { key: "missing_meta", n: needs.missing_meta, icon: Pencil, label: (n) => `${n} item${n > 1 ? "s" : ""} missing a title or description`, cta: "Open", go: () => onAttention("missing_metadata") },
    { key: "duplicate_alerts", n: needs.duplicate_alerts, icon: Copy, label: (n) => `${n} duplicate-upload group${n > 1 ? "s" : ""} detected`, cta: "Review duplicates", go: () => onAttention("duplicates") },
    { key: "reported", n: needs.reported, icon: Flag, label: (n) => `${n} open report${n > 1 ? "s" : ""} to moderate`, cta: "Moderate", go: () => onGoto("reports") },
  ].filter((a) => (a.n || 0) > 0);

  const quick = [
    { l: "Add Content", icon: UploadCloud, go: () => onGoto("upload") },
    { l: "Review Inbox", icon: Inbox, go: onInbox, badge: s.inbox },
    { l: "Uploads & Processing", icon: Clock, go: () => onGoto("uploads") },
    { l: "AI Caption Studio", icon: Sparkles, go: () => onGoto("caption-studio") },
    { l: "Galleries", icon: Layers, go: () => onGoto("galleries") },
    { l: "Homepage Control", icon: Home, go: () => onGoto("homepage"), optional: true },
  ];

  const actIcon = { media_uploaded: UploadCloud, media_edited: Pencil, community_submission: Inbox, caption_taught: Sparkles, new_user: Users, video_processed: Film };
  const actText = (a) => {
    if (a.type === "new_user") return <>New member <span className="text-white">{a.title}</span> joined</>;
    if (a.type === "caption_taught") return <>Caption example approved</>;
    if (a.type === "media_edited") return <><span className="text-white">{a.title}</span> edited</>;
    if (a.type === "video_processed") return <><span className="text-white">{a.title}</span> video processing completed</>;
    if (a.type === "community_submission") return <>Community submission <span className="text-white">{a.title}</span>{a.who ? <> by @{a.who}</> : null}</>;
    return <><span className="text-white">{a.title}</span> uploaded{a.who ? <> by @{a.who}</> : null}</>;
  };

  const card = "bg-[#141414] border border-[#262626] rounded-xl";

  return (
    <div className="space-y-4 max-w-[1400px] min-w-0" data-testid="admin-dashboard">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[#d9a24a]"><Shield className="w-4 h-4" /><span className="text-[10px] tracking-[0.2em] uppercase font-bold">Owner Control Center</span></div>
        <button onClick={() => { load(); refreshStats?.(); }} className="text-[11px] text-[#8b8b8b] hover:text-white inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>

      {/* 1 — Top stats + compact library breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5" data-testid="dash-stats">
        {statCards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className={`${card} px-3.5 py-3 ${c.hot ? "border-[#d9a24a]/50" : ""}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">{c.label}</span>
              <c.icon className={`w-3.5 h-3.5 ${c.hot ? "text-[#d9a24a]" : "text-[#8b8b8b]"}`} />
            </div>
            <div className={`text-2xl font-mono tracking-tight mt-1 ${c.hot ? "text-[#d9a24a]" : "text-white"}`}>{fmt(c.value)}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#8b8b8b] px-1" data-testid="dash-libraries">
        <span className="text-[10px] uppercase tracking-[0.1em] font-medium">Libraries</span>
        {libRow.map(([k, l], i) => (
          <React.Fragment key={k}>
            {i > 0 && <span className="text-[#262626]">·</span>}
            <button onClick={() => onGoto("content")} data-testid={`lib-count-${k}`} className="hover:text-white transition-colors">{l} <span className="font-mono text-[#e5e5e5]">{fmt(libs[k] ?? 0)}</span></button>
          </React.Fragment>
        ))}
      </div>

      {/* 2 — Needs attention (collapses to one line when nothing is actionable) */}
      <div className={`${card} px-4 py-3`} data-testid="dash-needs-attention">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-[#d9a24a]" />
          <span className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">Needs Attention</span>
          {attention.length > 0 && <span className="text-[10px] font-bold bg-[#d9a24a]/20 text-[#d9a24a] rounded-full px-1.5">{attention.length}</span>}
        </div>
        {attention.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-[#10b981]" data-testid="dash-all-good">
            <CheckCircle2 className="w-4 h-4" /> Everything looks good
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-1.5">
            {attention.map((a) => (
              <div key={a.key} data-testid={`needs-${a.key}`} className="flex items-center justify-between gap-3 rounded-lg bg-[#0f0f0f] border border-[#262626] px-3 py-1.5 min-w-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <a.icon className={`w-4 h-4 text-[#d9a24a] shrink-0 ${a.spin ? "animate-spin" : ""}`} />
                  <span className="text-sm text-[#e5e5e5] truncate">{a.label(a.n)}</span>
                </div>
                <button onClick={a.go} data-testid={`needs-${a.key}-cta`} className="shrink-0 inline-flex items-center gap-1 text-xs text-[#d9a24a] hover:text-white transition-colors">
                  {a.cta} <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3 + 4 — Quick actions (links only) and compact recent activity, side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 min-w-0">
        <div className="lg:col-span-2" data-testid="dash-quick-actions">
          <span className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">Quick Actions</span>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {quick.map((a) => (
              <button key={a.l} data-testid={`qa-${a.l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`} onClick={a.go}
                className={`relative inline-flex items-center gap-2 rounded-lg border border-[#262626] bg-[#141414] px-3 py-2.5 text-sm hover:border-[#d9a24a]/50 hover:text-white text-[#c9c9c9] transition-colors text-left min-w-0 ${a.optional ? "opacity-80" : ""}`}>
                <a.icon className="w-4 h-4 text-[#d9a24a] shrink-0" /> <span className="truncate">{a.l}</span>
                {a.badge > 0 && <span className="ml-auto text-[10px] font-bold bg-[#d9a24a] text-black rounded-full px-1.5">{a.badge}</span>}
              </button>
            ))}
          </div>
          <CaptionShortcut onOpen={() => onGoto("caption-studio")} />
        </div>

        <div className={`${card} overflow-hidden lg:col-span-3 min-w-0`} data-testid="dash-recent-activity">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#262626]">
            <h3 className="text-xs font-medium flex items-center gap-1.5 uppercase tracking-[0.1em] text-[#8b8b8b]"><Clock className="w-3.5 h-3.5 text-[#d9a24a]" /> Recent Activity</h3>
            <button onClick={() => onGoto("content")} className="text-xs text-[#d9a24a] hover:underline">View all</button>
          </div>
          {recent.length === 0 ? (
            <div className="p-5 text-center text-[#8b8b8b] text-sm">No recent activity.</div>
          ) : (
            <div className="divide-y divide-[#262626]/60">
              {recent.map((a, i) => {
                const I = actIcon[a.type] || Clock;
                return (
                  <div key={i} data-testid={`activity-${i}`} className={cn("flex items-center gap-2.5 px-4 py-1.5", a.id && "cursor-pointer hover:bg-[#262626]/20 transition-colors")} onClick={() => a.id && onOpen(a.id)}>
                    <I className="w-3.5 h-3.5 text-[#8b8b8b] shrink-0" />
                    <span className="text-xs text-[#c9c9c9] truncate flex-1">{actText(a)}</span>
                    <span className="text-[10px] text-[#8b8b8b] shrink-0">{timeAgo(a.when)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- Messages inbox (owner) ----------
const MessagesPanel = ({ onChange }) => {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => { try { const r = await api.get("/messages/threads"); setThreads(r.data); } catch {} }, []);
  const loadThread = useCallback(async (id) => { if (!id) return; try { const r = await api.get(`/messages/with/${id}`); setMsgs(r.data.messages); setOther(r.data.other); } catch {} }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (!active) return;
    loadThread(active);
    const t = setInterval(() => { loadThread(active); loadThreads(); onChange?.(); }, 3000);
    return () => clearInterval(t);
  }, [active, loadThread, loadThreads, onChange]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const send = async () => {
    const t = text.trim(); if (!t || !active) return;
    setSending(true);
    setMsgs((m) => [...m, { id: `tmp${Date.now()}`, from_id: user.user_id, text: t, created_at: new Date().toISOString() }]);
    setText("");
    try { await api.post("/messages", { to_id: active, text: t }); await loadThread(active); loadThreads(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[560px]">
      <div data-testid="admin-thread-list" className="bg-[#141414] border border-[#262626] rounded-xl overflow-y-auto">
        <div className="p-3 border-b border-[#262626] text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">Member messages</div>
        {threads.length === 0 ? <div className="p-8 text-center text-sm text-[#8b8b8b]">No messages yet. Members can DM you with suggestions.</div>
          : threads.map((t) => (
            <button key={t.other_id} data-testid={`admin-thread-${t.other_id}`} onClick={() => setActive(t.other_id)}
              className={cn("w-full flex items-center gap-3 p-3 border-b border-[#262626]/60 hover:bg-[#262626]/30 transition-colors text-left", active === t.other_id && "bg-[#262626]/40")}>
              <A2 className="w-9 h-9 border border-[#262626]"><A2I src={resolveAvatar(t.other_avatar)} /><A2F className="bg-secondary text-xs">{t.other_name?.[0]}</A2F></A2>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{t.other_name}</div>
                <div className={cn("text-xs truncate", t.unread ? "text-white font-medium" : "text-[#8b8b8b]")}>{t.last_from_me ? "You: " : ""}{t.last_text}</div>
              </div>
              {t.unread > 0 && <span className="text-[10px] font-bold bg-[#d9a24a] text-black rounded-full px-1.5 shrink-0">{t.unread}</span>}
            </button>
          ))}
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-xl flex flex-col overflow-hidden">
        {!active ? <div className="flex-1 flex items-center justify-center text-[#8b8b8b] text-sm">Select a conversation</div>
          : <>
            <div className="p-3 border-b border-[#262626] flex items-center gap-3">
              <A2 className="w-8 h-8 border border-[#262626]"><A2I src={resolveAvatar(other?.avatar_url)} /><A2F className="bg-secondary text-xs">{other?.name?.[0]}</A2F></A2>
              <span className="text-sm font-medium">{other?.name}</span>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgs.map((m) => {
                const mine = m.from_id === user.user_id;
                return <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}><div className={cn("max-w-[75%] rounded-2xl px-4 py-2 text-sm", mine ? "bg-[#d9a24a] text-black rounded-br-sm" : "bg-[#262626] text-white rounded-bl-sm")}><p className="whitespace-pre-wrap break-words">{m.text}</p></div></div>;
              })}
            </div>
            <div className="p-3 border-t border-[#262626] flex items-end gap-2">
              <textarea data-testid="admin-message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Reply…" className="flex-1 resize-none bg-[#0a0a0a] border border-[#262626] rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-[#d9a24a]/50" />
              <Button data-testid="admin-message-send" onClick={send} disabled={sending || !text.trim()} className="rounded-full h-11 w-11 p-0 shrink-0">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
            </div>
          </>}
      </div>
    </div>
  );
};

// ---------- Card helper ----------
const Panel = ({ title, action, children }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="flex items-center justify-between p-4 border-b border-border">
      <h3 className="font-heading text-lg">{title}</h3>{action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const useFetch = (url, dep = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      // One silent retry for transient blips (network/cookie race). If it still fails,
      // flag error so the UI can show a Retry instead of an endless spinner.
      try { const r2 = await api.get(url); setData(r2.data); }
      catch (e2) { setError(true); }
    } finally { setLoading(false); }
  }, [url]); // eslint-disable-line
  useEffect(() => { load(); }, dep); // eslint-disable-line
  return { data, loading, error, reload: load, setData };
};

// ---------- Settings ----------
const SettingsPanel = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Settings saved"); }
    catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-border last:border-0">
      <span className="text-sm">{label}</span>{children}
    </div>
  );
  const Sel = ({ k, opts }) => (
    <select data-testid={`set-${k}`} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })}
      className="bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary">
      {opts.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
    </select>
  );
  const Toggle = ({ k }) => (
    <button data-testid={`set-${k}`} onClick={() => setS({ ...s, [k]: !s[k] })}
      className={cn("w-11 h-6 rounded-full transition-colors relative", s[k] ? "bg-primary" : "bg-secondary")}>
      <span className={cn("absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all", s[k] ? "left-5" : "left-0.5")} />
    </button>
  );
  const Num = ({ k }) => (
    <input type="number" data-testid={`set-${k}`} value={s[k]} onChange={(e) => setS({ ...s, [k]: Number(e.target.value) })}
      className="w-24 bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary" />
  );
  return (
    <Panel title="Site Settings" action={<Button data-testid="settings-save" onClick={save} disabled={saving} size="sm" className="rounded-full">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}</Button>}>
      <Row label="Membership Approval"><Sel k="membership_approval" opts={[["auto", "Automatic"], ["manual", "Manual Approval"]]} /></Row>
      <Row label="Daily Photo Limit (per member)"><Num k="upload_limit_photos" /></Row>
      <Row label="Daily Video Limit (per member)"><Num k="upload_limit_videos" /></Row>
      <Row label="Auto-Publish Community Content"><Toggle k="auto_publish" /></Row>
      <Row label="Watermark"><Sel k="watermark" opts={[["off", "Off"], ["bottom-right", "BareFootage (Bottom Right)"], ["bottom-left", "Bottom Left"], ["center", "Center"]]} /></Row>
      <Row label="Comment Permissions"><Sel k="comment_permissions" opts={[["members", "Members Only"], ["everyone", "Everyone (signed in)"]]} /></Row>
      <Row label="Email Notifications"><Toggle k="email_notifications" /></Row>
    </Panel>
  );
};

// ---------- Ranking ----------
const RankingPanel = ({ onChange }) => {
  const [s, setS] = useState(null);
  const [recalc, setRecalc] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async (patch) => { const next = { ...s, ...patch }; setS(next); try { await api.put("/admin/settings", patch); } catch {} };
  const doRecalc = async () => { setRecalc(true); try { const r = await api.post("/admin/recalculate"); toast.success(`Recalculated ${r.data.count} items`); onChange?.(); } finally { setRecalc(false); } };
  if (!s) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  return (
    <div className="space-y-4">
      <Panel title="Ranking Controls">
        <div className="flex items-center justify-between py-2 border-b border-border">
          <span className="text-sm">Rating Scale</span><span className="text-sm text-muted-foreground">1 – 10 Stars (fixed)</span>
        </div>
        <div className="flex items-center justify-between py-3 border-b border-border">
          <span className="text-sm">Minimum Votes to rank</span>
          <input type="number" data-testid="rank-minvotes" value={s.rating_min_votes} onChange={(e) => save({ rating_min_votes: Number(e.target.value) })}
            className="w-24 bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary" />
        </div>
        <div className="flex items-center justify-between py-3 border-b border-border">
          <span className="text-sm">Featured Boost (%)</span>
          <input type="number" data-testid="rank-boost" value={s.featured_boost} onChange={(e) => save({ featured_boost: Number(e.target.value) })}
            className="w-24 bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary" />
        </div>
        <div className="flex items-center justify-between py-3">
          <span className="text-sm">Default Sort Order</span>
          <select data-testid="rank-sort" value={s.default_sort} onChange={(e) => save({ default_sort: e.target.value })}
            className="bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary">
            {[["recent", "Most Recent"], ["trending", "Trending"], ["top", "Top Rated"], ["most", "Most Rated"]].map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}
          </select>
        </div>
      </Panel>
      <Button data-testid="recalc-btn" onClick={doRecalc} disabled={recalc} className="rounded-full">{recalc ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Recalculate Rankings Now</Button>
    </div>
  );
};

// ---------- Tags ----------
const BannedWordsPanel = () => {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/ai/banned").then((r) => setText((r.data.words || []).join(", "))).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    const words = text.split(/[\n,]+/).map((w) => w.trim().toLowerCase()).filter(Boolean);
    try { const r = await api.put("/ai/banned", { words }); setText((r.data.words || []).join(", ")); toast.success("Banned words saved"); if ((r.data.refused_owner_terminology || []).length) toast.error(`Not banned (owner taxonomy terminology): ${r.data.refused_owner_terminology.join(", ")}`); }
    catch { toast.error("Couldn't save"); }
    setSaving(false);
  };
  return (
    <div className="mb-7 rounded-lg bg-secondary/30 border border-border p-4" data-testid="banned-words-panel">
      <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> Banned Words</div>
      <p className="text-xs text-muted-foreground mb-3">Words the AI must NEVER use in titles or descriptions (Rewrite, auto-captions and chat all obey this). Separate with commas or new lines.</p>
      <textarea data-testid="banned-words-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder="comfy, glancing, folks…" className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary resize-none mb-2" />
      <Button data-testid="banned-words-save" onClick={save} disabled={saving} size="sm" className="rounded-full">{saving ? "Saving…" : "Save banned words"}</Button>
    </div>
  );
};

const SessionsPanel = ({ onOpen }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [picked, setPicked] = useState({});   // groupIdx -> Set of tags to apply
  const load = async () => {
    setData(null);
    try {
      const r = await api.get("/admin/sessions/review");
      const init = {}; (r.data.groups || []).forEach((g, gi) => { init[gi] = new Set(g.suggested_tags); });
      setData(r.data); setPicked(init);
    } catch { setData({ groups: [] }); }
  };
  useEffect(() => { load(); }, []);
  const toggle = (gi, tag) => setPicked((p) => {
    const s = new Set(p[gi] || []); s.has(tag) ? s.delete(tag) : s.add(tag);
    return { ...p, [gi]: s };
  });
  const applyGroup = async (gi, group) => {
    const tags = Array.from(picked[gi] || new Set(group.suggested_tags));
    if (tags.length === 0) { toast.error("Pick at least one tag"); return; }
    setBusy(gi);
    try {
      await api.post("/admin/media/bulk_tags", { ids: group.ids, add: tags });
      toast.success(`Added ${tags.length} tag${tags.length === 1 ? "" : "s"} to all ${group.size} shots`);
      await load(); setPicked({});
    } catch { toast.error("Couldn't apply tags"); }
    setBusy(null);
  };
  const groups = data?.groups || [];
  return (
    <Panel title="Same session — to review">
      <p className="text-xs text-muted-foreground mb-4">These look like the <b>same woman in one session</b> (near-identical shots grouped by image match). Where a tag is on some shots but missing on others, tap it and hit <b>Apply to all</b> so the whole set matches. <button onClick={load} className="text-primary hover:underline">Refresh</button></p>
      {!data ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
        : groups.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">Nothing to review — your grouped shots all have matching tags.</p>
        : (
        <div className="space-y-6" data-testid="sessions-list">
          {groups.map((g, gi) => (
            <div key={gi} data-testid={`session-group-${gi}`} className="rounded-xl border border-border bg-card/40 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-heading">{g.size} shots · same session</div>
                {g.suggested_tags.length > 0 && (
                  <Button size="sm" data-testid={`session-apply-${gi}`} onClick={() => applyGroup(gi, g)} disabled={busy === gi} className="rounded-full">
                    {busy === gi ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Tag className="w-4 h-4 mr-1" />} Apply to all {g.size}
                  </Button>
                )}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                {g.items.map((m) => (
                  <button key={m.id} onClick={() => onOpen(m.id, g.items)} className="shrink-0 w-24 h-32 rounded-lg overflow-hidden bg-black border border-border hover:border-primary/60">
                    <img src={`${fileUrl(m.file_ref)}?w=200`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              {g.suggested_tags.length > 0 ? (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Missing on some — tap to include, then Apply</div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.suggested_tags.map((t) => {
                      const on = (picked[gi] || new Set()).has(t);
                      return (
                        <button key={t} data-testid={`session-tag-${gi}-${t}`} onClick={() => toggle(gi, t)}
                          className={"text-xs rounded-full px-3 py-1 border transition-colors " + (on ? "bg-primary text-primary-foreground border-primary" : "bg-secondary/50 border-border text-muted-foreground")}>
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : <p className="text-xs text-muted-foreground">All shots already share the same tags.</p>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

const TombstonesPanel = () => {
  const { data, reload } = useFetch("/admin/tombstones");
  const list = Array.isArray(data) ? data : [];
  const forget = async (mid) => { try { await api.delete(`/admin/tombstones/${mid}`); toast.success("This photo may be uploaded again"); reload(); } catch { toast.error("Couldn't remove the block"); } };
  if (!list.length) return null;
  return (
    <div className="mt-6 border-t border-border pt-4" data-testid="tombstones-panel">
      <div className="text-xs uppercase tracking-widest text-primary">Forgotten photos</div>
      <p className="text-xs text-muted-foreground mt-1 mb-2">Permanently deleted photos are remembered (exact + re-encoded copies) so they can't silently come back. "Allow again" lifts the block for one photo.</p>
      <ul className="space-y-1">
        {list.map((t) => (
          <li key={t.media_id} data-testid={`tombstone-${t.media_id}`} className="flex items-center justify-between gap-3 text-xs bg-secondary/30 rounded-lg px-3 py-2">
            <span className="truncate"><span className="font-medium">{t.title || "untitled"}</span> <span className="text-muted-foreground">· {t.reason} · {t.at ? new Date(t.at).toLocaleDateString() : ""}</span></span>
            <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]" data-testid={`tombstone-allow-${t.media_id}`} onClick={() => forget(t.media_id)}>Allow again</Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TrashPanel = ({ onChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const r = await api.get("/admin/trash"); setData(r.data); } catch { setData({ items: [], retention_days: 30 }); } };
  useEffect(() => { load(); }, []);
  const restore = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/trash/${id}/restore`); toast.success("Restored"); await load(); onChange?.(); }
    catch { toast.error("Couldn't restore"); }
    setBusy(false);
  };
  const purge = async (id) => {
    if (!window.confirm("Permanently delete this forever? This cannot be undone. The photo will also be remembered so it cannot silently come back through a re-upload.")) return;
    setBusy(true);
    try { await api.delete(`/admin/trash/${id}`); toast.success("Deleted forever"); await load(); }
    catch { toast.error("Couldn't delete"); }
    setBusy(false);
  };
  const items = data?.items || [];
  return (
    <Panel title="Trash">
      <p className="text-xs text-muted-foreground mb-4">Deleted posts land here and are kept for <b>{data?.retention_days || 30} days</b>, then permanently removed automatically. Restore anything before then, or delete it forever now.</p>
      {!data ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
        : items.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">Trash is empty.</p>
        : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="trash-grid">
          {items.map((m) => (
            <div key={m.id} data-testid={`trash-item-${m.id}`} className="rounded-xl border border-border bg-card/40 overflow-hidden">
              <div className="relative aspect-[3/4] bg-black">
                {m.media_type === "video"
                  ? <video src={videoUrl(m)} poster={posterUrl(m, 300)} muted preload="metadata" className="w-full h-full object-cover opacity-80" />
                  : <img src={`${fileUrl(m.file_ref)}?w=300`} alt="" className="w-full h-full object-cover opacity-80" />}
                <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-black/70">
                  {m.days_left != null ? `${m.days_left}d left` : "in trash"}
                </span>
              </div>
              <div className="p-2 space-y-1.5">
                <div className="text-xs font-heading truncate">{displayTitle(m.title) || "Untitled"}</div>
                <div className="flex gap-1.5">
                  <Button size="sm" data-testid={`trash-restore-${m.id}`} onClick={() => restore(m.id)} disabled={busy} className="flex-1 h-7 rounded-full text-xs">
                    <RotateCcw className="w-3 h-3 mr-1" /> Restore
                  </Button>
                  <Button size="icon" variant="outline" data-testid={`trash-purge-${m.id}`} onClick={() => purge(m.id)} disabled={busy}
                    className="h-7 w-7 rounded-full border-destructive/40 text-destructive hover:text-destructive"><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <TombstonesPanel />
    </Panel>
  );
};

const MasterRegistry = () => {
  const [reg, setReg] = useState(null);
  const [openCat, setOpenCat] = useState({});
  const [addingCat, setAddingCat] = useState(null);
  const [addVal, setAddVal] = useState("");
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [ncName, setNcName] = useState("");
  const [ncTag, setNcTag] = useState("");
  const load = () => api.get("/tags/registry").then((r) => setReg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const addTag = async (category, tag) => {
    const t = (tag || "").trim();
    if (!t) return;
    try {
      const r = await api.post("/tags/create", { category, tag: t });
      const d = r.data;
      if (d.created) toast.success(`Added "${d.tag}" to ${category}`);
      else if (d.restored) toast.success(`Restored "${d.tag}" in ${d.category}`);
      else if (d.tag !== d.input) toast.warning(`"${d.input}" was NOT created — it resolves to the existing canonical "${d.tag}" (${d.resolved_via} match). Remove that alias in the registry if "${d.input}" should be its own tag.`, { duration: 12000 });
      else toast.message(`"${d.tag}" already exists in ${d.category}`);
      if (d.post_save_warnings?.length) toast.warning(`Tag saved, maintenance warning: ${d.post_save_warnings.join("; ")}`);
      load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "object" && det ? (det.message || JSON.stringify(det)) : (det || e?.message || "no response");
      toast.error(`Couldn't add "${t}" to ${category} — HTTP ${e?.response?.status ?? "network"}: ${msg}`, { duration: 12000 });
      load();   // the record may still have been written before the error — always show the real registry state
    }
    setAddingCat(null); setAddVal("");
  };
  const deactivate = async (tag, active) => {
    try { await api.post("/tags/deactivate", { tag, active }); toast.success(active ? `"${tag}" is active again` : `"${tag}" deactivated — kept for history, no longer selectable`); load(); } catch { toast.error("Couldn't update"); }
  };
  // Permanent delete: impact preview → explicit confirmation → removed everywhere + tombstoned
  const [delImpact, setDelImpact] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const openDelete = async (tag) => {
    try { const r = await api.get(`/tags/master/${encodeURIComponent(tag)}/impact`); setDelImpact(r.data); }
    catch { toast.error("Couldn't load impact"); }
  };
  const confirmDelete = async () => {
    if (!delImpact) return;
    setDelBusy(true);
    try {
      const r = await api.post("/tags/delete-permanent", { tag: delImpact.tag, confirm: true });
      toast.success(`Deleted "${delImpact.tag}" everywhere — ${r.data.media_affected} media, ${r.data.smart_galleries_cleaned} smart galleries, ${r.data.aliases_removed} aliases cleaned`);
      setDelImpact(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
    setDelBusy(false);
  };
  const mergeTag = async (from) => {
    const into = window.prompt(`Merge "${from}" INTO which canonical tag? (all media keep their tags, "${from}" is folded in)`);
    if (!into) return;
    try { await api.post("/admin/tags/merge", { from, to: into.trim().toLowerCase() }); toast.success(`Merged "${from}" → "${into}"`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || "Merge failed"); }
  };
  return (
    <div className="mb-7 rounded-lg bg-secondary/30 border border-border p-4" data-testid="master-categories-tags">
      <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5 justify-between">
        <span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> Master Categories &amp; Tags {reg && <span className="text-muted-foreground normal-case tracking-normal">· {reg.total_tags} tags</span>}</span>
        <button data-testid="registry-add-category" onClick={() => { setNewCatOpen(true); setNcName(""); setNcTag(""); }} className="normal-case tracking-normal inline-flex items-center gap-1 rounded-full border border-primary/40 text-primary px-2 py-0.5 hover:bg-primary/10"><Plus className="w-3 h-3" /> Add category</button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">The single source of truth. Every editor, Smart Galleries, AI tagging and search read from this. Counts show how many media use each tag.</p>
      {newCatOpen && (
        <div className="flex gap-2 mb-3" data-testid="registry-new-category-row">
          <input data-testid="registry-newcat-name" value={ncName} onChange={(e) => setNcName(e.target.value)} placeholder="Category name" className="bg-secondary/50 border border-border rounded-lg px-2 py-1.5 text-xs outline-none" />
          <input data-testid="registry-newcat-tag" value={ncTag} onChange={(e) => setNcTag(e.target.value)} placeholder="first tag" className="bg-secondary/50 border border-border rounded-lg px-2 py-1.5 text-xs outline-none" />
          <Button size="sm" className="rounded-full" data-testid="registry-newcat-save" onClick={() => { if (ncName.trim() && ncTag.trim()) { addTag(ncName.trim(), ncTag.trim()); setNewCatOpen(false); } else toast.error("Name + first tag"); }}>Create</Button>
        </div>
      )}
      {!reg ? <p className="text-xs text-muted-foreground">Loading…</p> : (<>
        <div className="space-y-1.5">
          {reg.categories.map((c) => (
            <div key={c.category} className="rounded-lg border border-border bg-secondary/40" data-testid={`registry-cat-${c.category}`}>
              <button onClick={() => setOpenCat((s) => ({ ...s, [c.category]: !s[c.category] }))} data-testid={`registry-cat-toggle-${c.category}`}
                className="w-full flex items-center justify-between px-3 py-2 text-sm">
                <span className="font-medium">{c.category} <span className="text-[10px] text-muted-foreground">({c.tags.length}){c.single ? " · one" : ""}</span></span>
                <span className="text-muted-foreground text-xs">{openCat[c.category] ? "▾" : "▸"}</span>
              </button>
              {openCat[c.category] && (
                <div className="px-3 pb-3 space-y-1">
                  {c.tags.map((t) => (
                    <div key={t.tag} data-testid={`registry-tag-${t.tag}`} className={`flex items-center justify-between text-xs rounded px-2 py-1 ${t.active ? "bg-black/20" : "bg-black/10 opacity-50"}`}>
                      <span className="truncate">{t.tag} <span className="text-muted-foreground">· {t.count} media</span>{t.aliases.length ? <span className="text-muted-foreground/70"> · aka {t.aliases.slice(0, 4).join(", ")}</span> : ""}{!t.active && <span className="text-amber-300"> · INACTIVE (historical only — hidden from pickers)</span>}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <button data-testid={`registry-merge-${t.tag}`} onClick={() => mergeTag(t.tag)} className="text-muted-foreground hover:text-primary">merge</button>
                        <button data-testid={`registry-deact-${t.tag}`} onClick={() => deactivate(t.tag, !t.active)} className="text-muted-foreground hover:text-amber-300">{t.active ? "deactivate" : "reactivate"}</button>
                        <button data-testid={`registry-delete-${t.tag}`} onClick={() => openDelete(t.tag)} className="text-red-400/80 hover:text-red-300" title="Delete permanently (everywhere)">delete permanently</button>
                      </span>
                    </div>
                  ))}
                  {addingCat === c.category ? (
                    <input autoFocus data-testid={`registry-addtag-input-${c.category}`} value={addVal} onChange={(e) => setAddVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addTag(c.category, addVal); if (e.key === "Escape") { setAddingCat(null); setAddVal(""); } }}
                      onBlur={() => addTag(c.category, addVal)} placeholder="new tag…" className="text-xs rounded px-2 py-1 border border-primary/50 bg-secondary/60 outline-none w-32" />
                  ) : (
                    <button data-testid={`registry-addtag-${c.category}`} onClick={() => { setAddingCat(c.category); setAddVal(""); }} className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add tag</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <AlertDialog open={!!delImpact} onOpenChange={(o) => !o && setDelImpact(null)}>
          <AlertDialogContent data-testid="tag-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete “{delImpact?.tag}” permanently?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>This will remove this tag everywhere in BareFootage.</p>
                  <ul className="text-xs space-y-0.5 text-muted-foreground list-disc pl-4">
                    <li>Category: <b className="text-foreground">{delImpact?.category || "—"}</b></li>
                    <li>Media using it: <b className="text-foreground" data-testid="tag-delete-media-count">{delImpact?.media_count ?? 0}</b> (removed from manual, AI, rejected, effective and legacy tags)</li>
                    <li>Smart Galleries referencing it: <b className="text-foreground" data-testid="tag-delete-smart-count">{delImpact?.smart_gallery_count ?? 0}</b>{delImpact?.smart_galleries?.length ? ` — ${delImpact.smart_galleries.map((g) => g.name).join(", ")}` : ""}</li>
                    <li>Aliases pointing to it: <b className="text-foreground">{delImpact?.aliases?.length ?? 0}</b>{delImpact?.aliases?.length ? ` — ${delImpact.aliases.join(", ")}` : ""}</li>
                  </ul>
                  <p className="text-xs text-amber-300">The AI will never recreate this tag. You can re-add it later yourself if needed. Prefer <i>merge</i> if the media should keep an equivalent tag.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="tag-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="tag-delete-confirm" disabled={delBusy} onClick={(e) => { e.preventDefault(); confirmDelete(); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete Tag Everywhere</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>)}
    </div>
  );
};


const CategoryManager = () => {
  const [cats, setCats] = useState([]);
  const [extra, setExtra] = useState([]);
  const [catInput, setCatInput] = useState({});
  const [newCat, setNewCat] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const reload = () => api.get("/tags/categories").then((r) => setCats(r.data || [])).catch(() => {});
  useEffect(() => { reload(); }, []);
  const allCats = [...cats.map((c) => c.category), ...extra.filter((e) => !cats.some((c) => c.category === e))];
  const catTags = (name) => (cats.find((c) => c.category === name)?.tags) || [];
  const addTag = async (cat, tag) => {
    const t = (tag || "").trim().toLowerCase(); if (!t) return;
    try { const r = await api.post("/tags/master/move", { tag: t, category: cat }); setCats(r.data.categories || cats); setCatInput((s) => ({ ...s, [cat]: "" })); toast.success(`Added "${t}" to ${cat}`); }
    catch { toast.error("Couldn't add tag"); }
  };
  const removeTag = async (t) => {
    try { await api.delete(`/tags/master/${encodeURIComponent(t)}`); reload(); } catch { toast.error("Couldn't remove"); }
  };
  const addCategory = () => { const n = newCat.trim(); if (!n) return; if (!allCats.includes(n)) setExtra((e) => [...e, n]); setNewCat(""); };
  const renameCat = async (old) => {
    const n = renameVal.trim(); setRenaming(null); if (!n || n === old) return;
    try { const r = await api.post("/tags/category/rename", { old, new: n }); setCats(r.data.categories || cats); setExtra((e) => e.map((x) => (x === old ? n : x))); }
    catch { toast.error("Couldn't rename"); }
  };
  const deleteCat = async (name) => {
    if (!window.confirm(`Delete category "${name}"? Its tags leave your list (photos keep their tags).`)) return;
    try { const r = await api.delete(`/tags/category/${encodeURIComponent(name)}`); setCats(r.data.categories || cats); setExtra((e) => e.filter((x) => x !== name)); toast.success(`Deleted "${name}"`); }
    catch { toast.error("Couldn't delete"); }
  };
  const catSingle = (name) => !!(cats.find((c) => c.category === name)?.single);
  const toggleMode = async (name) => {
    try { const r = await api.post("/tags/category/select-mode", { category: name, single: !catSingle(name) }); setCats(r.data.categories || cats); }
    catch { toast.error("Couldn't change mode"); }
  };
  return (
    <div className="mb-7 rounded-lg bg-secondary/30 border border-border p-4" data-testid="category-manager">
      <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Categories &amp; Tags</div>
      <p className="text-xs text-muted-foreground mb-3">Build your tagging vocabulary. Add a category, then add tags under it — this is the exact list shown in the photo tagging workspace and the smart-gallery builder.</p>
      <div className="flex gap-2 mb-4">
        <input data-testid="new-cat-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
          placeholder="New category name (e.g. Vibe)" className="flex-1 bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary" />
        <Button data-testid="new-cat-add" onClick={addCategory} size="sm" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Add category</Button>
      </div>
      <div className="space-y-3">
        {allCats.map((name) => (
          <div key={name} data-testid={`catmgr-${name}`} className="rounded-lg border border-border p-3 bg-black/20">
            <div className="flex items-center justify-between mb-2 gap-2">
              {renaming === name ? (
                <input autoFocus data-testid={`catmgr-rename-${name}`} value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onBlur={() => renameCat(name)} onKeyDown={(e) => e.key === "Enter" && renameCat(name)}
                  className="bg-secondary/50 border border-border rounded px-2 py-1 text-sm" />
              ) : (
                <button onClick={() => { setRenaming(name); setRenameVal(name); }} className="text-xs font-bold uppercase tracking-widest text-foreground/90 hover:text-primary flex items-center gap-1">{name} <Pencil className="w-3 h-3 opacity-50" /></button>
              )}
              <button data-testid={`catmgr-del-${name}`} onClick={() => deleteCat(name)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <button data-testid={`catmgr-mode-${name}`} onClick={() => toggleMode(name)}
              className={cn("mb-2 inline-flex items-center gap-1.5 text-[10px] rounded-full px-2 py-1 border transition-colors",
                catSingle(name) ? "bg-primary/15 border-primary/40 text-primary" : "bg-secondary/50 border-border text-muted-foreground hover:text-foreground")}>
              {catSingle(name) ? "◉ Single-select (pick one)" : "◎ Multi-select (pick many)"}
            </button>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {catTags(name).map((t) => (
                <span key={t} data-testid={`catmgr-tag-${t}`} className="inline-flex items-center gap-1 text-[12px] rounded-full bg-primary/15 border border-primary/30 text-primary px-2 py-0.5">
                  {t}<button onClick={() => removeTag(t)} data-testid={`catmgr-tag-remove-${t}`} className="opacity-60 hover:opacity-100"><X className="w-3 h-3" /></button>
                </span>
              ))}
              {catTags(name).length === 0 && <span className="text-[11px] text-muted-foreground">No tags yet — add some below.</span>}
            </div>
            <div className="flex gap-2">
              <input data-testid={`catmgr-addtag-input-${name}`} value={catInput[name] || ""} onChange={(e) => setCatInput((s) => ({ ...s, [name]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(name, catInput[name]); } }}
                placeholder="add a tag + Enter" className="flex-1 bg-secondary/50 border border-border rounded-lg px-2.5 py-1.5 text-sm outline-none focus:border-primary" />
              <Button data-testid={`catmgr-addtag-btn-${name}`} onClick={() => addTag(name, catInput[name])} size="sm" variant="outline" className="rounded-full border-border"><Plus className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const VIDEO_STATE_ROWS = [
  ["processing_now", "Processing now", "text-foreground"], ["processed_bf", "Successfully processed (bf)", "text-primary"],
  ["legacy_cloudinary", "Legacy Cloudinary playback", "text-[#d9a24a]"], ["needs_processing", "Needs processing / no playback", "text-foreground"],
  ["failed", "Failed", "text-destructive"], ["stuck", "Stuck", "text-destructive"], ["pending", "Pending verification", "text-foreground"],
];

const VideoStateBreakdown = ({ s }) => (
  <div className="mb-3" data-testid="video-summary">
    <div className={`text-sm font-medium mb-2 ${s.all_processed ? "text-[#10b981]" : "text-[#d9a24a]"}`} data-testid="video-summary-headline">
      {s.remaining === 0
        ? (s.legacy_cloudinary > 0 ? `${s.legacy_cloudinary} video${s.legacy_cloudinary === 1 ? "" : "s"} still on legacy Cloudinary playback — not yet migrated to bf` : "Everything has been processed")
        : `${s.remaining} video${s.remaining === 1 ? "" : "s"} still need${s.remaining === 1 ? "s" : ""} processing${s.legacy_cloudinary > 0 ? ` · ${s.legacy_cloudinary} on legacy Cloudinary` : ""}`}
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {VIDEO_STATE_ROWS.map(([k, label, cls]) => (
        <span key={k} className="text-muted-foreground" data-testid={`video-state-${k}`}>{label}: <span className={`font-semibold ${s[k] > 0 ? cls : ""}`}>{s[k] ?? 0}</span></span>
      ))}
      <span className="text-muted-foreground" data-testid="video-state-total">Total videos: <span className="font-semibold">{s.total}</span></span>
    </div>
  </div>
);

const urlKind = (u) => (!u ? "none" : u.includes("/api/files/d/") ? "bf" : u.includes("res.cloudinary.com") ? "cloudinary" : "other");
const MIGRATE_STATUS_LABEL = { migrated: "migrated", failed: "failed", skipped_already_bf: "skipped already bf", refused_not_video: "refused", already_running: "already running", not_found: "not found" };

const MigrationRow = ({ r }) => {
  const a = r.after || {}, b = r.before || {};
  const bad = r.status === "failed" || r.status === "refused_not_video" || r.status === "not_found";
  return (
    <tr className="border-t border-border/40 align-top" data-testid={`migrate-row-${r.media_id}`}>
      <td className="py-1.5 pr-3 font-mono text-[11px]">{r.media_id.slice(0, 8)}…</td>
      <td className="py-1.5 pr-3 max-w-[200px] truncate" title={b.title || a.title || ""}>{b.title || a.title || "—"}</td>
      <td className="py-1.5 pr-3">{b.video_backend || "legacy"} · {b.state || "—"}</td>
      <td className={`py-1.5 pr-3 font-semibold ${bad ? "text-destructive" : r.status === "migrated" ? "text-[#10b981]" : "text-[#d9a24a]"}`} data-testid={`migrate-status-${r.media_id}`}>{MIGRATE_STATUS_LABEL[r.status] || r.status}</td>
      <td className="py-1.5 pr-3">{a.video_backend || b.video_backend || "legacy"}</td>
      <td className="py-1.5 pr-3">{urlKind(a.cloud_video_url ?? b.cloud_video_url)}</td>
      <td className="py-1.5 pr-3">{urlKind(a.cloud_poster_url ?? b.cloud_poster_url)}</td>
      <td className="py-1.5 pr-3">{String(a.video_failed ?? b.video_failed ?? false)}</td>
      <td className="py-1.5 pr-3 font-mono text-[11px]">{a.video_rev || "—"}</td>
      <td className="py-1.5 text-destructive max-w-[260px] truncate" title={a.video_error || r.result?.error || ""}>{a.video_error || r.result?.error || ""}</td>
    </tr>
  );
};

const RecoveryWorkerPanel = ({ onChanged }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/videos/recovery-status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const act = async (action) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/videos/recovery", { action });
      toast.success(action === "pause" ? "Recovery paused — the current video finishes, then the worker idles" : action === "resume" ? "Recovery resumed — the worker picks the oldest outstanding video within ~20 s" : `${r.data.unparked} parked video${r.data.unparked === 1 ? "" : "s"} queued again`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update recovery"); }
    finally { setBusy(false); }
  };
  if (!st) return null;
  const running = !st.paused;
  return (
    <div className="mb-4 rounded-lg border border-border/60 bg-background/40 p-3" data-testid="recovery-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="text-xs tracking-widest uppercase text-primary flex items-center gap-1.5"><RefreshCw className={`w-3.5 h-3.5 ${running && st.current ? "animate-spin" : ""}`} /> Background bf recovery worker</div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold ${running ? "text-[#10b981]" : "text-[#d9a24a]"}`} data-testid="recovery-state">{running ? (st.current ? "Running — processing" : `Running — ${st.wait_reason || "idle"}`) : "Paused"}</span>
          <Button data-testid="recovery-toggle-btn" size="sm" variant={running ? "outline" : "default"} className="rounded-full" disabled={busy} onClick={() => act(running ? "pause" : "resume")}>
            {running ? "Pause" : "Resume"}
          </Button>
          {st.parked > 0 && <Button data-testid="recovery-retry-parked-btn" size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => act("retry_parked")}>Retry {st.parked} parked</Button>}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">Server-side and resumable: no browser needed. One replica holds the lease and processes <b>one</b> outstanding video at a time through the same verified bf path (original → H.264 + poster → public HEAD/Range check → atomic cutover). Legacy Cloudinary videos count as outstanding. Failures retry after 10 min, 1 h, then park. Cloudinary fields and originals are never modified.</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span data-testid="recovery-outstanding">Outstanding: <b className="text-foreground">{st.outstanding}</b></span>
        <span data-testid="recovery-due">Due now: <b className="text-foreground">{st.due_now}</b></span>
        <span data-testid="recovery-parked">Parked after 3 failures: <b className={st.parked > 0 ? "text-destructive" : "text-foreground"}>{st.parked}</b></span>
        <span data-testid="recovery-run-counts">This run: <b className="text-[#10b981]">{st.processed_this_run} ok</b> · <b className={st.failed_this_run > 0 ? "text-destructive" : ""}>{st.failed_this_run} failed</b></span>
        <span data-testid="recovery-lease">Lease: <b className="text-foreground">{st.lease?.active ? (st.lease.held_by_this_replica ? "this replica" : (st.lease.owner || "").split(":")[0].slice(-12)) : "none"}</b></span>
        {st.next_due_at && <span data-testid="recovery-next-due">Next retry due: <b className="text-foreground">{new Date(st.next_due_at).toLocaleTimeString()}</b></span>}
      </div>
      {st.current && <div className="mt-2 text-xs" data-testid="recovery-current">Now: <span className="font-mono text-[11px]">{st.current.media_id.slice(0, 8)}…</span> {st.current.title || ""} <span className="text-muted-foreground">(was {st.current.state_before})</span></div>}
      {st.last && <div className="mt-1 text-xs text-muted-foreground" data-testid="recovery-last">Last: <span className="font-mono text-[11px]">{st.last.media_id.slice(0, 8)}…</span> {st.last.title || ""} → <b className={st.last.state === "processed_bf" ? "text-[#10b981]" : "text-destructive"}>{st.last.state}</b>{st.last.error ? ` — ${st.last.error}` : ""}{st.last.parked ? " (parked)" : ""}</div>}
    </div>
  );
};

const LegacyMigrationPanel = ({ onChanged }) => {
  const [oneId, setOneId] = useState("");
  const [size, setSize] = useState(10);
  const [legacy, setLegacy] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [stopped, setStopped] = useState(null);
  const refresh = useCallback(async () => {
    try { const r = await api.get("/admin/videos/legacy-cloudinary", { params: { limit: 25 } }); setLegacy(r.data); } catch (e) {}
    onChanged?.();
  }, [onChanged]);
  useEffect(() => { refresh(); }, [refresh]);
  const migrateOne = async (id) => {
    const r = await api.post("/admin/videos/migrate-bf", { media_ids: [id], wait: true });
    const res = r.data.results?.[0] || { media_id: id, status: "failed", result: { error: "no result" } };
    setRows((prev) => [res, ...prev]);
    return res;
  };
  const runOne = async () => {
    const id = oneId.trim(); if (!id) return;
    setBusy(id); setStopped(null);
    try { const res = await migrateOne(id); toast[res.status === "migrated" ? "success" : res.status === "failed" ? "error" : "message"](`${id.slice(0, 8)}… → ${MIGRATE_STATUS_LABEL[res.status] || res.status}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Migration request failed"); }
    finally { setBusy(null); refresh(); }
  };
  const runBatch = async () => {
    setStopped(null);
    let items = [];
    try { items = (await api.get("/admin/videos/legacy-cloudinary", { params: { limit: size } })).data.items || []; } catch (e) { toast.error("Could not load legacy candidates"); return; }
    if (!items.length) { toast.message("No legacy Cloudinary videos left to migrate."); return; }
    let done = 0;
    for (const it of items) {
      setBusy(it.id);
      let res;
      try { res = await migrateOne(it.id); } catch (e) { res = { status: "failed", media_id: it.id, result: { error: e?.response?.data?.detail || "request failed" } }; setRows((p) => [res, ...p]); }
      if (res.status === "migrated") { done += 1; refresh(); continue; }
      if (res.status === "skipped_already_bf") continue;
      setStopped({ id: it.id, status: res.status }); toast.error(`Batch stopped at ${it.id.slice(0, 8)}… (${MIGRATE_STATUS_LABEL[res.status] || res.status})`); break;
    }
    setBusy(null); refresh();
    if (done) toast.success(`${done} video${done === 1 ? "" : "s"} migrated to bf`);
  };
  return (
    <div className="rounded-lg border border-border/60 bg-card/40 p-4 mb-3" data-testid="legacy-migration-panel">
      <div className="text-xs font-semibold tracking-widest uppercase text-primary mb-1">Legacy Cloudinary → bf migration</div>
      <p className="text-xs text-muted-foreground mb-3">Operator surface over <code>POST /api/admin/videos/migrate-bf</code>. One video at a time, <code>wait=true</code>; the record stays on its legacy state until the new H.264 + poster pass HEAD / Range / poster delivery checks, then one atomic cutover. Cloudinary assets, <code>cloud_public_id</code> and <code>cloud_delivery_type</code> are never touched. A batch stops at the first failure.</p>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div className="flex items-end gap-2">
          <div><label className="block text-[11px] text-muted-foreground mb-1">Single media ID</label>
            <input value={oneId} onChange={(e) => setOneId(e.target.value)} placeholder="media id" className="h-9 w-[320px] rounded-md border border-border bg-background px-2 text-xs font-mono" data-testid="migrate-one-input" /></div>
          <Button size="sm" onClick={runOne} disabled={!!busy || !oneId.trim()} data-testid="migrate-one-btn">{busy && busy === oneId.trim() ? <Loader2 className="w-4 h-4 animate-spin" /> : "Migrate to bf"}</Button>
        </div>
        <div className="flex items-end gap-2">
          <div><label className="block text-[11px] text-muted-foreground mb-1">Batch size</label>
            <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="h-9 rounded-md border border-border bg-background px-2 text-xs" data-testid="migrate-batch-size"><option value={10}>10</option><option value={25}>25</option></select></div>
          <Button size="sm" variant="outline" onClick={runBatch} disabled={!!busy || !legacy || legacy.healthy_legacy === 0} data-testid="migrate-batch-btn">{busy && busy !== oneId.trim() ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run next batch"}</Button>
          <span className="text-xs text-muted-foreground" data-testid="legacy-remaining">{legacy ? `${legacy.total_legacy} legacy Cloudinary video${legacy.total_legacy === 1 ? "" : "s"} remaining${legacy.failed_legacy ? ` (${legacy.failed_legacy} failed — run individually)` : ""}` : "…"}</span>
        </div>
      </div>
      {busy && <div className="text-xs text-[#d9a24a] mb-2" data-testid="migrate-busy">Migrating {busy.slice(0, 8)}… (download → H.264 → poster → upload → verify → cutover)</div>}
      {stopped && <div className="text-xs text-destructive mb-2" data-testid="migrate-stopped">Batch stopped at <span className="font-mono">{stopped.id}</span> — {MIGRATE_STATUS_LABEL[stopped.status] || stopped.status}. Nothing after it was attempted.</div>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="migrate-results">
            <thead><tr className="text-left text-muted-foreground"><th className="pr-3">Media</th><th className="pr-3">Title</th><th className="pr-3">Start state</th><th className="pr-3">Result</th><th className="pr-3">Backend</th><th className="pr-3">Video URL</th><th className="pr-3">Poster URL</th><th className="pr-3">Failed</th><th className="pr-3">Revision</th><th>Error</th></tr></thead>
            <tbody>{rows.map((r, i) => <MigrationRow key={`${r.media_id}-${i}`} r={r} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const VideoProcessingPanel = () => {
  const [summary, setSummary] = useState(null);
  const [bulkVid, setBulkVid] = useState(null);
  const bulkPoll = useRef(null);
  const loadSummary = useCallback(() => api.get("/admin/videos/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  const fetchBulkVid = useCallback(async () => {
    try {
      const r = await api.get("/admin/videos/reprocess-stuck/status");
      setBulkVid(r.data);
      loadSummary();
      if (!r.data.running && bulkPoll.current) { clearInterval(bulkPoll.current); bulkPoll.current = null; }
    } catch {}
  }, [loadSummary]);
  useEffect(() => { fetchBulkVid(); return () => { if (bulkPoll.current) clearInterval(bulkPoll.current); }; }, [fetchBulkVid]);
  const startBulkVid = async () => {
    try {
      const r = await api.post("/admin/videos/reprocess-stuck", { limit: 100 });
      if (r.data.queued === 0) {
        const s = (await api.get("/admin/videos/summary")).data; setSummary(s);
        toast[s.remaining === 0 ? "success" : "message"](s.remaining === 0 ? "Every video has a playable version." : `Auto-recovery queue is empty, but ${s.remaining} video${s.remaining === 1 ? "" : "s"} still need${s.remaining === 1 ? "s" : ""} processing (see the status breakdown).`);
        fetchBulkVid(); return;
      }
      toast.success(`Reprocessing ${r.data.queued} video${r.data.queued === 1 ? "" : "s"} one at a time — memory-safe`);
      if (!bulkPoll.current) bulkPoll.current = setInterval(fetchBulkVid, 2000);
      fetchBulkVid();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start reprocessing"); }
  };
  const cancelBulkVid = async () => {
    try { await api.post("/admin/videos/reprocess-stuck/cancel"); toast.message("Cancelling after the current video finishes…"); fetchBulkVid(); }
    catch { toast.error("Couldn't cancel"); }
  };
  return (
    <div className="mb-6 rounded-lg bg-secondary/30 border border-primary/25 p-4" data-testid="reprocess-stuck-panel">
      <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Reprocess All Videos Safely</div>
      <p className="text-xs text-muted-foreground mb-3">Fixes every video that has no playable version — <b>one at a time</b> through the BareFootage H.264 pipeline. Memory-safe: each clip is streamed to disk (never loaded whole into RAM), so the site stays responsive even with large 100MB videos. Already-playable videos are skipped. A failed video is logged and the queue keeps going. You can cancel any time.</p>
      {summary && <VideoStateBreakdown s={summary} />}
      <RecoveryWorkerPanel onChanged={loadSummary} />
      <LegacyMigrationPanel onChanged={loadSummary} />
      <div className="flex items-center gap-2 flex-wrap">
        <Button data-testid="reprocess-stuck-start" onClick={startBulkVid} disabled={bulkVid?.running} size="sm" className="rounded-full">
          {bulkVid?.running ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Processing…</> : <><RefreshCw className="w-4 h-4 mr-1" /> Reprocess All Videos Safely</>}
        </Button>
        {bulkVid?.running && (
          <Button data-testid="reprocess-stuck-cancel" onClick={cancelBulkVid} size="sm" variant="outline" className="rounded-full border-destructive/40 text-destructive hover:text-destructive">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
        {!bulkVid?.running && typeof bulkVid?.pending_total === "number" && (
          <span className="text-xs text-muted-foreground" data-testid="reprocess-pending-count">{bulkVid.pending_total} in the auto-recovery queue</span>
        )}
      </div>
      {bulkVid && (bulkVid.running || bulkVid.done > 0) && (
        <div className="mt-3" data-testid="reprocess-stuck-progress">
          <div className="h-2 rounded-full bg-black/40 overflow-hidden border border-border">
            <div className="h-full bg-primary transition-all" style={{ width: `${bulkVid.total ? Math.round((bulkVid.done / bulkVid.total) * 100) : 0}%` }} />
          </div>
          {bulkVid.running ? (
            <div className="text-sm text-foreground mt-2" data-testid="reprocess-now-processing">
              Processing {Math.min(bulkVid.done + 1, bulkVid.total)} of {bulkVid.total}
              {bulkVid.current_title && <div className="text-primary font-medium truncate">{bulkVid.current_title}</div>}
            </div>
          ) : (
            <div className="text-sm text-primary font-medium mt-2" data-testid="reprocess-complete-summary">
              Reprocessing complete — {bulkVid.ok} successful{bulkVid.failed > 0 ? `, ${bulkVid.failed} failed` : ""}
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1">
            Completed: <span className="text-primary">{bulkVid.ok}</span> · Failed: <span className={bulkVid.failed > 0 ? "text-destructive" : ""}>{bulkVid.failed}</span> · Remaining: {Math.max(bulkVid.total - bulkVid.done, 0)}
          </div>
          {bulkVid.failures && bulkVid.failures.length > 0 && (
            <div className="mt-2 border-t border-border pt-2" data-testid="reprocess-failures">
              <div className="text-[11px] uppercase tracking-wide text-destructive mb-1">Failed videos</div>
              <ul className="space-y-1 max-h-40 overflow-auto">
                {bulkVid.failures.map((f) => (
                  <li key={f.id} className="text-[11px] text-muted-foreground" data-testid={`reprocess-failure-${f.id}`}>
                    <span className="text-foreground">{f.title}</span> — <span className="text-destructive break-all">{f.error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-3">Individual failed videos can also be retried from the media viewer (open the video → "Reprocess video"). RUN_VIDEO_RECOVERY stays off — nothing processes automatically at startup.</p>
    </div>
  );
};

const UploadsProcessing = ({ onOpen, onChange }) => (
  <Panel title="Uploads & Processing">
    <VideoProcessingPanel />
    <DuplicateMaintenancePanel onChange={onChange} />
    <div className="text-xs tracking-widest uppercase text-muted-foreground mb-3 mt-2">Pending uploads — to approve</div>
    <ContentLibrary onOpen={onOpen} onChange={onChange} initialStatus="pending" />
  </Panel>
);

const CAPTION_SUBS = [["overview", "Overview", Sparkles], ["training", "Training & Examples", BookOpen], ["testing", "Testing & Diagnostics", RefreshCw]];
const HEALTH_STYLE = { healthy: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", warning: "text-amber-300 border-amber-500/40 bg-amber-500/10", error: "text-red-400 border-red-500/40 bg-red-500/10" };

const CaptionStatusCards = ({ status, compact = false }) => (
  <div className={cn("grid gap-3", compact ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-4")} data-testid="caption-status-cards">
    {[["stage1", "Stage 1 · Facts", status?.stage1_model || "gpt-5.4"], ["stage2", "Stage 2 · Writer", status?.stage2_model || "gpt-5.4-mini"],
      ["examples", "Approved Examples", status ? `${status.approved_examples}` : "…"], ["prompt", "Prompt Version", status?.prompt_version || "—"]].map(([k, l, v]) => (
      <div key={k} className="rounded-lg bg-black/30 border border-border p-3 min-w-0" data-testid={`status-${k}`}>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
        <div className={cn("text-sm font-semibold mt-0.5 truncate", k === "examples" ? "text-primary" : "text-foreground")} title={String(v)}>{v}{k === "examples" && status ? <span className="text-[11px] font-normal ml-1.5"><span className="text-amber-300">· {status.gold_examples} gold</span><span className="text-muted-foreground"> · {status.auto_learned_recent} auto-learned</span></span> : null}</div>
      </div>
    ))}
  </div>
);

const CaptionOverview = ({ status }) => (
  <div data-testid="caption-overview">
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <div className="text-xs tracking-widest uppercase text-primary flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> Caption System</div>
      {status && <span data-testid="caption-health" className={cn("text-[11px] uppercase tracking-widest rounded-full border px-2.5 py-1 font-medium", HEALTH_STYLE[status.health] || HEALTH_STYLE.warning)}>{status.health}</span>}
    </div>
    <CaptionStatusCards status={status} />
    {status?.health_notes?.length ? <ul className="mt-3 text-xs text-amber-300 space-y-1" data-testid="caption-health-notes">{status.health_notes.map((n) => <li key={n}>· {n}</li>)}</ul> : null}
    <details className="mt-4 rounded-lg border border-border bg-black/20 p-3 text-xs" data-testid="caption-technical-details">
      <summary className="cursor-pointer text-muted-foreground uppercase tracking-widest text-[10px]">Technical details</summary>
      {status ? (
        <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 mt-2 text-muted-foreground">
          <dt>Examples store</dt><dd className="font-mono text-foreground/80 col-span-1 md:col-span-2">{status.source_collection} · filter {JSON.stringify(status.count_filter)}</dd>
          <dt>Approved (active)</dt><dd className="text-foreground/80 md:col-span-2">{status.approved_examples} of {status.total_examples} stored</dd>
          <dt>Gold / Pinned</dt><dd className="text-foreground/80 md:col-span-2">{status.gold_examples} (always injected, max 12)</dd>
          <dt>Auto-learned</dt><dd className="text-foreground/80 md:col-span-2">{status.auto_learned_recent} owner-saved captions remembered automatically (same library, lowest priority: Gold → relevant Approved → max 2 auto-learned)</dd>
          <dt>Caption Guide</dt><dd className="text-foreground/80 md:col-span-2">{status.guide_chars} chars</dd>
          <dt>LLM key</dt><dd className="text-foreground/80 md:col-span-2">{status.has_llm_key ? "present" : "MISSING"}</dd>
          <dt>Pipeline</dt><dd className="text-foreground/80 md:col-span-2">4-frame video Stage 1 · owner tag authority · nail-colour gate · factual validator · freshness reducer · truncation guard · relevance retrieval</dd>
        </dl>
      ) : <div className="mt-2 text-muted-foreground">Loading…</div>}
    </details>
  </div>
);

const CaptionExampleCard = ({ e, onGold, onActive, onDelete, onPromote }) => (
  <div className={cn("rounded-lg border p-2.5 text-xs", e.gold ? "bg-amber-500/5 border-amber-500/40" : "bg-secondary/50 border-border", !e.active && "opacity-60")} data-testid={`style-example-${e.id}`}>
    <div className="flex items-start justify-between gap-2 mb-1">
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-widest">
        <span className="text-sky-400/80">{e.media_type}</span>
        {e.gold && <span className="rounded-full border border-amber-500/50 text-amber-300 px-1.5 py-0.5 inline-flex items-center gap-1" data-testid={`style-example-gold-badge-${e.id}`}><Star className="w-2.5 h-2.5" aria-hidden="true" /> Gold / Pinned</span>}
        {!e.gold && e.kind === "approved" && <span className="rounded-full border border-sky-500/40 text-sky-300 px-1.5 py-0.5" data-testid={`style-example-kind-${e.id}`}>Approved</span>}
        {e.kind === "auto_learned" && <span className="rounded-full border border-border text-muted-foreground px-1.5 py-0.5" data-testid={`style-example-kind-${e.id}`}>Auto-learned</span>}
        {!e.active && <span className="rounded-full border border-border text-muted-foreground px-1.5 py-0.5">inactive</span>}
        {e.source === "legacy_gold" && <span className="text-muted-foreground">migrated gold text</span>}
        {e.source === "legacy_auto_learned" && <span className="text-muted-foreground">migrated from old auto-learn store</span>}
        {e.prompt_version && <span className="text-muted-foreground normal-case tracking-normal font-mono">{e.prompt_version}</span>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {e.kind === "auto_learned" && <button type="button" data-testid={`style-example-promote-${e.id}`} onClick={() => onPromote(e)} title="Promote to Approved (you vouch for it)" className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 text-sky-300 px-2 py-0.5 text-[11px] hover:bg-sky-500/10"><Check className="w-3 h-3" aria-hidden="true" /> Approve</button>}
        <button type="button" data-testid={`style-example-gold-${e.id}`} onClick={() => onGold(e)} title={e.gold ? "Unpin — back to normal relevance retrieval" : "Mark Gold / Pinned — permanent style anchor"}
          className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]", e.gold ? "border-amber-500/60 text-amber-300 hover:bg-amber-500/10" : "border-border text-muted-foreground hover:text-amber-300 hover:border-amber-500/50")}>
          <Star className={cn("w-3 h-3", e.gold && "fill-current")} aria-hidden="true" /> {e.gold ? "Gold" : "Mark gold"}
        </button>
        <button type="button" data-testid={`style-example-active-${e.id}`} onClick={() => onActive(e)} title={e.active ? "Deactivate (kept, not used)" : "Activate"} className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:text-foreground">{e.active ? "Deactivate" : "Activate"}</button>
        <Button data-testid={`style-example-delete-${e.id}`} onClick={() => onDelete(e)} size="icon" variant="ghost" className="h-6 w-6 text-destructive hover:text-destructive shrink-0" aria-label="Delete example"><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></Button>
      </div>
    </div>
    <div className="text-foreground/90" data-testid={`style-example-text-${e.id}`}>{e.approved_description}</div>
    <details className="mt-1.5" data-testid={`style-example-details-${e.id}`}>
      <summary className="text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">Retained with this example</summary>
      <div className="mt-1.5 space-y-1 text-muted-foreground">
        {e.rejected_description ? <div><span className="text-destructive/80 uppercase tracking-widest text-[10px]">Rejected / original AI</span><div className="italic mt-0.5">{e.rejected_description}</div></div> : <div className="italic">No rejected/original AI version stored.</div>}
        {e.visual_summary ? <div><span className="uppercase tracking-widest text-[10px]">Visual summary</span><div className="italic">{e.visual_summary}</div></div> : null}
        {e.facts && Object.keys(e.facts).length ? <div><span className="uppercase tracking-widest text-[10px]">Stage 1 facts</span><div className="font-mono break-words">{Object.entries(e.facts).filter(([, v]) => v && v !== "unclear" && typeof v !== "object").slice(0, 14).map(([k, v]) => `${k}=${v}`).join(" · ")}</div></div> : null}
        {(e.manual_tags?.length || e.effective_tags?.length) ? <div><span className="uppercase tracking-widest text-[10px]">Tags</span> <span>manual: {(e.manual_tags || []).join(", ") || "—"} · effective: {(e.effective_tags || []).join(", ") || "—"}</span></div> : null}
        <div className="font-mono text-[10px]">media {e.media_id || "— (text only)"} · {e.created_at?.slice(0, 10)}</div>
      </div>
    </details>
  </div>
);

const CaptionTraining = ({ status, reloadStatus }) => {
  const [capGuide, setCapGuide] = useState("");
  const [savingCap, setSavingCap] = useState(false);
  const [examples, setExamples] = useState(null);
  const [filter, setFilter] = useState("all"); // all | gold | approved | auto_learned | inactive
  const load = useCallback(() => api.get("/ai/caption-examples").then((r) => setExamples(r.data.examples || [])).catch(() => setExamples([])), []);
  useEffect(() => { load(); api.get("/ai/caption-training").then((r) => setCapGuide(r.data.guide || "")).catch(() => {}); }, [load]);
  const saveGuide = async () => {
    setSavingCap(true);
    try { await api.put("/ai/caption-training", { guide: capGuide }); toast.success("Caption Guide saved — every caption follows it"); reloadStatus(); }
    catch { toast.error("Couldn't save the Caption Guide"); }
    setSavingCap(false);
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); reloadStatus(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const list = (examples || []).filter((e) => filter === "all" ? true : filter === "inactive" ? !e.active : (e.kind === filter && e.active));
  const gold = (examples || []).filter((e) => e.gold && e.active).length;
  const approvedN = (examples || []).filter((e) => e.active && e.kind === "approved").length;
  const autoN = (examples || []).filter((e) => e.active && e.kind === "auto_learned").length;
  return (
    <div data-testid="caption-training-section" className="space-y-7">
      <div className="rounded-lg bg-secondary/30 border border-primary/30 p-4" data-testid="caption-guide-panel">
        <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" aria-hidden="true" /> Caption Guide / Rules</div>
        <p className="text-xs text-muted-foreground mb-3">The permanent rulebook injected into every generated caption, Rewrite and Refine. One authoritative copy — examples below teach voice, this teaches rules.</p>
        <Textarea data-testid="caption-guide-input" value={capGuide} onChange={(e) => setCapGuide(e.target.value)} rows={7}
          placeholder={"Paste your BareFootage caption rules here (lead with the strongest detail, soles first when visible, specific footwear, public visibility, playful speculation only, no heels)…"}
          className="bg-secondary/50 border-border resize-none mb-2 text-xs" />
        <Button data-testid="caption-training-save" onClick={saveGuide} disabled={savingCap} size="sm" className="rounded-full">
          {savingCap ? <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" /> : <Save className="w-4 h-4 mr-1" aria-hidden="true" />} Save Caption Guide
        </Button>
      </div>
      <div className="rounded-lg bg-secondary/30 border border-sky-500/30 p-4" data-testid="style-library-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
          <div className="text-xs tracking-widest uppercase text-sky-400 flex items-center gap-1.5"><BookmarkPlus className="w-3.5 h-3.5" aria-hidden="true" /> Examples Library <span className="text-muted-foreground" data-testid="examples-counts">({gold} gold · {approvedN} approved · {autoN} auto-learned)</span></div>
          <div className="flex items-center gap-1.5">
            {[["all", "All"], ["gold", "Gold / Pinned"], ["approved", "Approved"], ["auto_learned", "Auto-learned"], ["inactive", "Inactive"]].map(([v, l]) => (
              <button key={v} type="button" data-testid={`examples-filter-${v}`} aria-pressed={filter === v} onClick={() => setFilter(v)} className={cn("text-[11px] rounded-full px-2.5 py-1 border", filter === v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground")}>{l}</button>
            ))}
            <button type="button" data-testid="examples-refresh" onClick={load} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 ml-1"><RefreshCw className="w-3 h-3" aria-hidden="true" /> Refresh</button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">ONE library, three strengths. <b className="text-amber-300">Gold / Pinned</b> = your permanent anchors, always injected. <b className="text-sky-300">Approved</b> = created by <b>Approve &amp; Teach</b> (original AI text, Stage 1 facts, visual summary, tags, prompt version retained), used by relevance. <b>Auto-learned</b> = captions you saved that the older system remembered automatically — voice only, lowest priority (never outweighs Approved), promotable to Approved.</p>
        {!examples ? <div className="text-xs text-muted-foreground">Loading…</div>
          : list.length ? (
            <div className="space-y-2 max-h-[34rem] overflow-auto pr-1" data-testid="style-library-list">
              {list.map((e) => (
                <CaptionExampleCard key={e.id} e={e}
                  onGold={(x) => act(() => api.post(`/ai/caption-examples/${x.id}/gold`, { gold: !x.gold }), x.gold ? "Unpinned — back to relevance retrieval" : "Marked Gold / Pinned")}
                  onActive={(x) => act(() => api.post(`/ai/caption-examples/${x.id}/active`, { active: !x.active }), x.active ? "Example deactivated" : "Example activated")}
                  onPromote={(x) => act(() => api.post(`/ai/caption-examples/${x.id}/promote`), "Promoted to Approved")}
                  onDelete={(x) => { if (window.confirm("Delete this example from the library? The media item itself is not touched.")) act(() => api.delete(`/ai/caption-examples/${x.id}`), "Example deleted"); }} />
              ))}
            </div>
          ) : <p className="text-xs text-muted-foreground italic" data-testid="style-library-empty">{filter === "all" ? "No approved examples yet. Open an item in the media editor, refine a description you love, then tap Approve & Teach." : "Nothing in this filter."}</p>}
      </div>
      <div data-testid="caption-writing-rules">
        <div className="text-xs tracking-widest uppercase text-muted-foreground mb-1">Writing Rules</div>
        <p className="text-xs text-muted-foreground mb-3">Hard constraints the caption writer must obey on every Rewrite, auto-caption and AI chat/title/description.</p>
        <StyleNotesPanel />
        <BannedWordsPanel />
      </div>
    </div>
  );
};

// Plain-English style notes injected into the caption writer's system prompt (moved here from the removed AI tagging block).
const StyleNotesPanel = () => {
  const [rules, setRules] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/ai/tag-rules").then((r) => setRules(r.data.text || "")).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/ai/tag-rules", { text: rules }); toast.success("Style notes saved — every caption follows them"); }
    catch { toast.error("Couldn't save notes"); }
    setSaving(false);
  };
  return (
    <div className="mb-5 rounded-lg bg-secondary/30 border border-border p-4" data-testid="caption-style-notes">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Style notes (plain English)</div>
      <p className="text-xs text-muted-foreground mb-2">Rules the caption writer follows on every Rewrite and auto-caption, e.g. "call the strap-between-the-toes ones flip flops", "never say slides". Captions only — these never touch your tags.</p>
      <Textarea data-testid="ai-rules-input" value={rules} onChange={(e) => setRules(e.target.value)} rows={3} placeholder={"one rule per line…"} className="bg-secondary/50 border-border resize-none mb-2" />
      <Button data-testid="ai-rules-save" onClick={save} disabled={saving} size="sm" className="rounded-full">
        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" /> : <Save className="w-4 h-4 mr-1" aria-hidden="true" />} Save style notes
      </Button>
    </div>
  );
};

const VERDICT_STYLE = { pass: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", warning: "text-amber-300 border-amber-500/40 bg-amber-500/10", failure: "text-red-400 border-red-500/40 bg-red-500/10", error: "text-red-400 border-red-500/40 bg-red-500/10" };
const CHECK_LABELS = { factual_validator: "Factual validator", nail_colour_confidence: "Nail-colour confidence", subject_scope: "Subject scope", footwear_conflict: "Footwear conflict", truncation: "Truncation / incomplete", template_phrases: "Template phrases" };

const CaptionTesting = ({ status }) => {
  const [regRunning, setRegRunning] = useState(false);
  const [reg, setReg] = useState(null);
  const [regLimit, setRegLimit] = useState(8);
  const runRegression = async () => {
    setRegRunning(true); setReg(null);
    try {
      const r = await api.post("/ai/caption-regression", { limit: regLimit }, { timeout: 300000 });
      setReg(r.data);
      toast.success(`Tested ${r.data.count} — ${r.data.summary?.pass ?? 0} pass · ${r.data.summary?.warning ?? 0} warning · ${(r.data.summary?.failure ?? 0) + (r.data.summary?.error ?? 0)} failure`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Regression run failed"); }
    setRegRunning(false);
  };
  return (
    <div className="rounded-lg bg-secondary/30 border border-border p-4" data-testid="caption-regression-panel">
      <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Caption Regression Tool</div>
      <p className="text-xs text-muted-foreground mb-3">Runs the current Stage 1 ({status?.stage1_model || "gpt-5.4"}) + Stage 2 ({status?.stage2_model || "gpt-5.4-mini"}) pipeline on a sample of recent photos. Read-only: nothing is written to your media. Prompt version <span className="font-mono">{status?.prompt_version || "—"}</span>.</p>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select data-testid="regression-limit" value={regLimit} onChange={(e) => setRegLimit(Number(e.target.value))} className="bg-secondary/50 border border-border rounded-lg px-2 py-1.5 text-xs outline-none">
          {[2, 4, 8, 12, 20].map((n) => <option key={n} value={n}>{`${n} images`}</option>)}
        </select>
        <Button data-testid="regression-run" onClick={runRegression} disabled={regRunning} size="sm" className="rounded-full">
          {regRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" /> : <RefreshCw className="w-4 h-4 mr-1" aria-hidden="true" />} Run sample
        </Button>
        {regRunning && <span className="text-xs text-muted-foreground">Generating… roughly 10–20s per image.</span>}
        {reg?.summary && (
          <div className="flex items-center gap-1.5 text-[11px] ml-auto" data-testid="regression-summary">
            <span className="text-muted-foreground">{reg.count} tested</span>
            {["pass", "warning", "failure", "error"].map((k) => reg.summary[k] ? <span key={k} className={cn("rounded-full border px-2 py-0.5 uppercase tracking-widest", VERDICT_STYLE[k])}>{reg.summary[k]} {k}</span> : null)}
          </div>
        )}
      </div>
      {reg?.results?.length ? (
        <div className="space-y-3 max-h-[36rem] overflow-auto pr-1" data-testid="regression-results">
          {reg.results.map((r) => (
            <div key={r.id} className="flex gap-3 rounded-lg bg-secondary/50 border border-border p-2.5" data-testid={`regression-item-${r.id}`}>
              {r.file_ref ? <img src={thumbUrl(r.file_ref, 120)} alt="" className="w-16 h-20 object-cover rounded shrink-0" /> : null}
              <div className="min-w-0 text-xs flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span data-testid={`regression-verdict-${r.id}`} className={cn("text-[10px] uppercase tracking-widest rounded-full border px-2 py-0.5 font-medium", VERDICT_STYLE[r.verdict] || VERDICT_STYLE.warning)}>{r.verdict}</span>
                  <span className="font-mono text-[10px] text-muted-foreground">{r.prompt_version}</span>
                </div>
                {r.error ? <div className="text-destructive">Error: {r.error}</div> : <>
                  <div className="font-medium text-foreground">{r.generated_title}</div>
                  <div className="text-muted-foreground mt-0.5">{r.generated_description}</div>
                  <div className="flex flex-wrap gap-1 mt-2" data-testid={`regression-checks-${r.id}`}>
                    {Object.entries(r.checks || {}).map(([k, c]) => (
                      <span key={k} title={JSON.stringify(c)} className={cn("text-[10px] rounded-full border px-2 py-0.5 inline-flex items-center gap-1", c.ok ? "border-emerald-500/30 text-emerald-400/90" : "border-amber-500/50 text-amber-300")}>
                        {c.ok ? <Check className="w-2.5 h-2.5" aria-hidden="true" /> : <AlertTriangle className="w-2.5 h-2.5" aria-hidden="true" />} {CHECK_LABELS[k] || k}
                        {!c.ok && (c.mismatches?.length || c.issues?.length || c.hits?.length) ? <span className="opacity-80">· {(c.mismatches || c.issues || c.hits).slice(0, 2).join("; ")}</span> : null}
                      </span>
                    ))}
                  </div>
                  {r.facts?.footwear ? <div className="text-[10px] text-muted-foreground/80 mt-1.5">facts: {r.facts.footwear}{r.facts.setting && r.facts.setting !== "unclear" ? ` · ${r.facts.setting}` : ""}{r.facts.position && r.facts.position !== "unclear" ? ` · ${r.facts.position}` : ""}{r.facts.primary_subject_count ? ` · subjects ${r.facts.primary_subject_count}` : ""}{r.facts.toenail_confidence ? ` · nail conf ${r.facts.toenail_confidence}` : ""}</div> : null}
                  <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: r.anatomical_side_determinable === false ? "#f59e0b" : "#22c55e" }}>
                    anatomical side {r.anatomical_side_determinable === false ? "uncertain → screen-side language" : "determinable"}
                  </div>
                </>}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const CaptionStudioPanel = ({ sub = "overview", setSub }) => {
  const [status, setStatus] = useState(null);
  const loadStatus = useCallback(() => api.get("/ai/caption-status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  return (
    <Panel title="AI Caption Studio">
      <div className="flex flex-wrap gap-2 mb-5" data-testid="caption-subnav" role="tablist">
        {CAPTION_SUBS.map(([v, l, Icon]) => (
          <button key={v} type="button" role="tab" aria-selected={sub === v} data-testid={`caption-sub-${v}`} onClick={() => setSub(v)}
            className={cn("inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border transition-colors", sub === v ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />{l}
          </button>
        ))}
      </div>
      {sub === "overview" && <CaptionOverview status={status} />}
      {sub === "training" && <CaptionTraining status={status} reloadStatus={loadStatus} />}
      {sub === "testing" && <CaptionTesting status={status} />}
    </Panel>
  );
};


// ---------- Smart Galleries (Admin → Galleries → Smart Galleries) ----------
const SmartGalleriesPanel = ({ initialEditId = null, onEditConsumed }) => {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [excl, setExcl] = useState(new Set());
  const [tqName, setTqName] = useState("");
  const [tqVis, setTqVis] = useState("public");
  const [tqPreview, setTqPreview] = useState(null);
  const [tqBusy, setTqBusy] = useState(false);
  const [tqMediaType, setTqMediaType] = useState("all");   // all | image | video (real media_type field, not a tag)
  const [tqLibrary, setTqLibrary] = useState("all");       // all | originals | celebrities | pornstars | community
  const tqDeb = useRef(null);
  useEffect(() => { api.get("/tags/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);
  const buildQuery = (s, x) => {
    const parts = [];
    for (const c of cats) {
      const chosen = c.tags.filter((t) => s.has(t));
      if (chosen.length === 1) parts.push(`+${chosen[0]}`);
      else if (chosen.length > 1) parts.push(`+(${chosen.join(" or ")})`);
    }
    for (const t of x) parts.push(`-${t}`);
    return parts.join(" ");
  };
  const tqQuery = buildQuery(sel, excl);
  const tqScope = [tqMediaType !== "all" ? (tqMediaType === "image" ? "Photos" : "Videos") : null, tqLibrary !== "all" ? tqLibrary : null].filter(Boolean);
  const tqBody = { query: tqQuery.trim(), media_type: tqMediaType, library: tqLibrary === "all" ? null : tqLibrary };
  // tri-state: off -> include -> exclude -> off
  const toggleTag = (t) => {
    if (sel.has(t)) { setSel((p) => { const n = new Set(p); n.delete(t); return n; }); setExcl((p) => { const n = new Set(p); n.add(t); return n; }); }
    else if (excl.has(t)) { setExcl((p) => { const n = new Set(p); n.delete(t); return n; }); }
    else { setSel((p) => { const n = new Set(p); n.add(t); return n; }); }
  };
  useEffect(() => {
    if (tqDeb.current) clearTimeout(tqDeb.current);
    const q = tqQuery.trim();
    if (!q) { setTqPreview(null); return; }
    tqDeb.current = setTimeout(async () => {
      try { const r = await api.post("/collections/tag-query/preview", tqBody); setTqPreview(r.data); }
      catch (e) { setTqPreview({ error: e?.response?.data?.detail || "Invalid" }); }
    }, 350);
    return () => tqDeb.current && clearTimeout(tqDeb.current);
  }, [tqQuery, tqMediaType, tqLibrary]); // eslint-disable-line
  const createTq = async () => {
    const q = tqQuery.trim();
    if (!q) { toast.error("Pick at least one tag"); return; }
    setTqBusy(true);
    try {
      if (tqEditingId) {
        // EDIT: update the SAME gallery in place — never creates a second one
        await api.put(`/collections/tag-query/${tqEditingId}`, { ...tqBody, name: tqName.trim() || q, visibility: tqVis });
        toast.success("Smart gallery updated");
        resetBuilder(); loadMine();
      } else {
        const r = await api.post("/collections/tag-query", { ...tqBody, name: tqName.trim() || q, visibility: tqVis });
        toast.success("Smart gallery created");
        navigate(`/collection/${r.data.id}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || (tqEditingId ? "Could not update" : "Could not create")); }
    setTqBusy(false);
  };
  // ---- My Smart Galleries (manage saved rule-based galleries) ----
  const [tqEditingId, setTqEditingId] = useState(null);
  const [mine, setMine] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const builderRef = useRef(null);
  const loadMine = useCallback(() => api.get("/collections/tag-query").then((r) => setMine(r.data.galleries || [])).catch(() => setMine([])), []);
  useEffect(() => { loadMine(); }, [loadMine]);
  const resetBuilder = () => { setTqEditingId(null); setSel(new Set()); setExcl(new Set()); setTqName(""); setTqVis("public"); setTqMediaType("all"); setTqLibrary("all"); setTqPreview(null); };
  const startEdit = useCallback((g) => {
    // restore EVERY saved rule: name, visibility, library, media type, included + excluded tags
    setTqEditingId(g.id); setTqName(g.name || ""); setTqVis(g.visibility || "public");
    setTqMediaType(g.media_type || "all"); setTqLibrary(g.library || "all");
    setSel(new Set(g.include_tags || [])); setExcl(new Set(g.exclude_tags || []));
    setTimeout(() => builderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  }, []);
  useEffect(() => {
    if (!initialEditId || !mine) return;
    const g = mine.find((x) => x.id === initialEditId);
    if (g) startEdit(g); else toast.error("That smart gallery no longer exists");
    onEditConsumed && onEditConsumed();
  }, [initialEditId, mine]); // eslint-disable-line
  const duplicateTq = async (g) => {
    try { const r = await api.post(`/collections/tag-query/${g.id}/duplicate`); toast.success(`Created "${r.data.name}"`); loadMine(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not duplicate"); }
  };
  const deleteTq = async () => {
    const g = delTarget; if (!g) return;
    try { await api.delete(`/collections/${g.id}`); toast.success(`Deleted smart gallery "${g.name}" — no photos or videos were touched`); if (tqEditingId === g.id) resetBuilder(); loadMine(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    setDelTarget(null);
  };
  return (
    <div data-testid="smart-galleries-section">
      <div ref={builderRef} className={`mb-6 rounded-lg bg-secondary/30 border p-4 ${tqEditingId ? "border-amber-400/60" : "border-primary/30"}`} data-testid="smart-gallery-builder">
        <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> {tqEditingId ? "Edit Smart Gallery" : "Build a Smart Gallery"}</div>
        {tqEditingId && (
          <div className="mb-2 flex items-center justify-between gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 text-xs text-amber-200" data-testid="tq-editing-banner">
            <span>Editing <b>{tqName || "this gallery"}</b> — saving updates the same gallery (no duplicate is created).</span>
            <button type="button" data-testid="tq-cancel-edit" onClick={resetBuilder} className="text-amber-300 hover:text-white underline">Cancel edit</button>
          </div>
        )}
        <p className="text-xs text-muted-foreground mb-3">Click a tag once to <b className="text-primary">include</b>, again to <b className="text-red-400">exclude</b>, again to clear. It's <b>AND across categories, OR within</b>, and excluded tags are removed (e.g. <span className="font-mono">barefoot AND anklets NOT indoor</span>). Auto-updates as your library grows.</p>
        <div className="space-y-3 mb-3">
          <div data-testid="tq-media-type">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Media type</div>
            <div className="flex flex-wrap gap-1.5">
              {[["all", "All"], ["image", "Photos"], ["video", "Videos"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`tq-media-${v}`} aria-pressed={tqMediaType === v} onClick={() => setTqMediaType(v)}
                  className={`text-[12px] rounded-full px-2.5 py-1 border transition-colors ${tqMediaType === v ? "bg-primary text-primary-foreground border-primary font-medium" : "bg-secondary/50 border-border text-muted-foreground hover:text-foreground"}`}>
                  {l}
                </button>
              ))}
              <span className="text-[11px] text-muted-foreground self-center ml-1">Filters on the real photo/video type, not a tag. Default All keeps existing smart galleries unchanged.</span>
            </div>
          </div>
          <div data-testid="tq-library">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Library scope</div>
            <div className="flex flex-wrap gap-1.5">
              {[["all", "All libraries"], ["originals", "Originals"], ["celebrities", "Celebrities"], ["pornstars", "Pornstars"], ["web_finds", "Best of the Web"], ["community", "Community"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`tq-lib-${v}`} aria-pressed={tqLibrary === v} onClick={() => setTqLibrary(v)}
                  className={`text-[12px] rounded-full px-2.5 py-1 border transition-colors ${tqLibrary === v ? "bg-primary text-primary-foreground border-primary font-medium" : "bg-secondary/50 border-border text-muted-foreground hover:text-foreground"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {cats.map((c) => (
            <div key={c.category} data-testid={`cat-${c.category}`}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{c.category}</div>
              <div className="flex flex-wrap gap-1.5">
                {c.tags.map((t) => { const on = sel.has(t); const off = excl.has(t); return (
                  <button key={t} type="button" data-testid={`pick-${t}`} onClick={() => toggleTag(t)}
                    className={`text-[12px] rounded-full px-2.5 py-1 border transition-colors ${on ? "bg-primary text-primary-foreground border-primary font-medium" : off ? "bg-red-500/20 text-red-300 border-red-500/50 font-medium line-through" : "bg-secondary/50 border-border text-muted-foreground hover:text-foreground"}`}>
                    {off ? "−" : ""}{t}
                  </button>
                ); })}
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-md bg-black/40 border border-border px-3 py-2 mb-2 font-mono text-xs text-muted-foreground min-h-[34px]" data-testid="tq-generated">
          {tqScope.map((s) => <span key={s} className="text-primary mr-1.5" data-testid="tq-scope-chip">[{s}]</span>)}
          {tqQuery || "Your query builds here as you pick tags…"}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground" data-testid="tq-preview">
            {tqPreview?.error ? <span className="text-red-400">{tqPreview.error}</span>
              : tqPreview ? <>Matches <span className="text-primary font-semibold">{tqPreview.count}</span> {tqMediaType === "image" ? "photos" : tqMediaType === "video" ? "videos" : "photos/videos"}{tqLibrary !== "all" ? ` in ${tqLibrary}` : ""}</>
              : "Pick tags to preview…"}
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="tq-name" value={tqName} onChange={(e) => setTqName(e.target.value)} placeholder="Gallery name (optional)"
              className="w-44 bg-secondary/50 border border-border rounded-lg px-3 py-1.5 text-sm outline-none focus:border-primary" />
            <select data-testid="tq-vis" value={tqVis} onChange={(e) => setTqVis(e.target.value)} className="bg-secondary/50 border border-border rounded-lg px-2 py-1.5 text-xs outline-none">
              <option value="public">Public</option><option value="private">Private</option>
            </select>
            <Button data-testid="tq-create" onClick={createTq} disabled={tqBusy || !tqQuery || !!tqPreview?.error} size="sm" className="rounded-full">
              {tqBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : tqEditingId ? <><Save className="w-4 h-4 mr-1" aria-hidden="true" /> Save changes</> : <><Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Create</>}
            </Button>
          </div>
        </div>
        {tqPreview?.thumbs?.length > 0 && (
          <div className="flex gap-1.5 mt-3">
            {tqPreview.thumbs.map((t) => <div key={t.id} className="w-12 h-12 rounded overflow-hidden bg-black border border-border"><img src={thumbUrl(t.file_ref, 120)} alt="" className="w-full h-full object-cover" /></div>)}
          </div>
        )}
      </div>
      <div className="mb-2 rounded-lg bg-secondary/30 border border-border p-4" data-testid="my-smart-galleries">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-xs tracking-widest uppercase text-primary flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" aria-hidden="true" /> My Smart Galleries {mine ? <span className="text-muted-foreground">({mine.length})</span> : null}</div>
          <button type="button" data-testid="my-smart-refresh" onClick={loadMine} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" aria-hidden="true" /> Refresh</button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Rule-based (virtual) galleries you saved. Edit reloads the rules into the builder above; Delete removes only the gallery definition — never any photos or videos.</p>
        {!mine ? <div className="text-xs text-muted-foreground">Loading…</div>
          : mine.length === 0 ? <div className="text-xs text-muted-foreground" data-testid="my-smart-empty">No smart galleries yet — build one above.</div>
          : (
            <div className="space-y-2">
              {mine.map((g) => (
                <div key={g.id} data-testid={`smart-row-${g.id}`} className={`rounded-md border px-3 py-2.5 flex flex-wrap items-start gap-3 ${tqEditingId === g.id ? "border-amber-400/60 bg-amber-500/5" : "border-border bg-black/30"}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button type="button" onClick={() => navigate(`/collection/${g.id}`)} className="text-sm font-medium hover:text-primary truncate" data-testid="smart-row-name">{g.name}</button>
                      <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-border text-muted-foreground" data-testid="smart-row-library">{g.library === "all" ? "All libraries" : g.library}</span>
                      <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border border-border text-muted-foreground" data-testid="smart-row-media-type">{g.media_type === "image" ? "Photos" : g.media_type === "video" ? "Videos" : "All"}</span>
                      <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${g.visibility === "public" ? "border-green-500/40 text-green-400" : "border-border text-muted-foreground"}`} data-testid="smart-row-status">{g.visibility === "public" ? "Active · Public" : "Private"}</span>
                      <span className="text-xs text-muted-foreground" data-testid="smart-row-count"><b className="text-primary">{g.count}</b> item{g.count === 1 ? "" : "s"}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1" data-testid="smart-row-tags">
                      {(g.include_tags || []).map((t) => <span key={`i-${t}`} className="text-[11px] rounded-full px-2 py-0.5 bg-primary/15 text-primary border border-primary/30">{t}</span>)}
                      {(g.exclude_tags || []).map((t) => <span key={`x-${t}`} className="text-[11px] rounded-full px-2 py-0.5 bg-red-500/15 text-red-300 border border-red-500/40 line-through">−{t}</span>)}
                      {!(g.include_tags || []).length && !(g.exclude_tags || []).length && <span className="text-[11px] text-muted-foreground font-mono">{g.query_text}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button type="button" data-testid="smart-row-edit" onClick={() => startEdit(g)} className="inline-flex items-center gap-1 text-xs rounded-full border border-border px-2.5 py-1 hover:border-primary/60 hover:text-primary"><Pencil className="w-3 h-3" aria-hidden="true" /> Edit</button>
                    <button type="button" data-testid="smart-row-duplicate" onClick={() => duplicateTq(g)} className="inline-flex items-center gap-1 text-xs rounded-full border border-border px-2.5 py-1 hover:border-primary/60 hover:text-primary"><Copy className="w-3 h-3" aria-hidden="true" /> Duplicate</button>
                    <button type="button" data-testid="smart-row-delete" onClick={() => setDelTarget(g)} className="inline-flex items-center gap-1 text-xs rounded-full border border-destructive/40 text-red-400 px-2.5 py-1 hover:bg-destructive/10"><Trash2 className="w-3 h-3" aria-hidden="true" /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
          <AlertDialogContent data-testid="smart-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{delTarget?.name}"?</AlertDialogTitle>
              <AlertDialogDescription>This deletes the virtual gallery definition only. It does NOT delete any photos or videos — every item stays in your library and in any other gallery.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="smart-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="smart-delete-confirm" onClick={deleteTq} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete Gallery</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

// ---------- Auto Organizer (Admin → Galleries → Auto Organizer) ----------
const AutoOrganizerPanel = () => {
  const [organizing, setOrganizing] = useState(false);
  const organize = async () => {
    setOrganizing(true);
    try { const r = await api.post("/admin/organize"); toast.success(`${r.data.system_galleries} smart galleries ready (+${r.data.new_city_galleries} cities)`); }
    catch { toast.error("Organize failed"); }
    setOrganizing(false);
  };
  return (
    <div className="rounded-lg bg-secondary/30 border border-border p-4" data-testid="auto-organizer-section">
      <div className="text-xs tracking-widest uppercase text-primary mb-1 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" aria-hidden="true" /> Auto Organizer</div>
      <p className="text-xs text-muted-foreground mb-3">Rebuild the automatic gallery hierarchy (Photos, Videos, Screenshots, Flip-Flops, Sandals, Barefoot, Multiple People, Exposed Toes, No Location + one per city) and refresh the tag-based automatic gallery views. Works across every content library and respects your gallery rules — it never deletes or re-tags anything.</p>
      <Button data-testid="organize-galleries" onClick={organize} disabled={organizing} size="sm" className="rounded-full">
        {organizing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" /> : <ImageIcon className="w-4 h-4 mr-1" aria-hidden="true" />} Organize galleries
      </Button>
    </div>
  );
};

// ---------- Duplicate maintenance (Admin → Uploads & Processing) ----------
const DuplicateMaintenancePanel = ({ onChange }) => {
  const [deduping, setDeduping] = useState(false);
  const [report, setReport] = useState(null);
  const dryRun = async () => {
    setDeduping(true); setReport(null);
    try { const r = await api.post("/admin/media/dedupe", { dry_run: true }); setReport(r.data); if (!r.data.duplicate_groups) toast.success("No exact duplicate uploads found — your library is clean."); }
    catch { toast.error("Couldn't run the duplicate report"); }
    setDeduping(false);
  };
  const execute = async () => {
    const n = report?.would_remove || 0;
    if (!n) return;
    if (!window.confirm(`Move ${n} exact duplicate cop${n === 1 ? "y" : "ies"} to Trash and keep the canonical record in each of the ${report.duplicate_groups} group${report.duplicate_groups === 1 ? "" : "s"}? Tags merge onto the kept copy, manual gallery memberships are repointed, ratings/comments stay restorable for 30 days.`)) return;
    setDeduping(true);
    try { const r = await api.post("/admin/media/dedupe", {}); toast.success(`Removed ${r.data.removed} duplicate${r.data.removed === 1 ? "" : "s"} across ${r.data.duplicate_groups} group${r.data.duplicate_groups === 1 ? "" : "s"}`); setReport(null); onChange?.(); }
    catch { toast.error("Couldn't run the duplicate cleanup"); }
    setDeduping(false);
  };
  return (
    <div className="mb-6 rounded-lg bg-secondary/30 border border-border p-4" data-testid="duplicate-maintenance-panel">
      <div className="text-xs tracking-widest uppercase text-muted-foreground mb-1 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" aria-hidden="true" /> Duplicate uploads</div>
      <p className="text-xs text-muted-foreground mb-3">Exact duplicates only — same SHA-256 of the original bytes or the same stored file. No perceptual/similar-image matching. <b>Dry run first</b>: the report below is read-only; nothing is removed until you press Execute.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="dedupe-media" onClick={dryRun} disabled={deduping} size="sm" variant="outline" className="rounded-full">
          {deduping ? <Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" /> : <Search className="w-4 h-4 mr-1" aria-hidden="true" />} Review duplicates (dry run)
        </Button>
        {report?.would_remove > 0 && <Button data-testid="dedupe-execute" onClick={execute} disabled={deduping} size="sm" variant="outline" className="rounded-full border-destructive/40 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4 mr-1" aria-hidden="true" /> Execute cleanup · {report.would_remove}</Button>}
      </div>
      {report && (
        <div className="mt-3 text-xs" data-testid="dedupe-report">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground mb-2" data-testid="dedupe-summary">
            <span><b className="text-foreground">{report.live_records}</b> live records</span>
            <span><b className="text-foreground">{report.duplicate_groups}</b> exact duplicate groups</span>
            <span><b className="text-foreground">{report.records_involved}</b> records involved</span>
            <span><b className="text-primary">{report.would_remove}</b> would be moved to Trash</span>
            <span>{report.affected_galleries} gallery refs · {report.affected_ratings} ratings · {report.affected_comments} comments on duplicates</span>
          </div>
          {report.groups?.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-auto pr-1">
              {report.groups.map((g) => (
                <div key={g.key} className="rounded-md border border-border bg-black/30 p-2.5" data-testid={`dedupe-group-${g.canonical.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1"><span className="uppercase tracking-widest text-[10px] text-primary">match: {g.match}</span>{g.in_no_location && <span className="rounded-full border border-amber-500/40 text-amber-300 px-1.5 py-0.5 text-[10px]">in No Location</span>}{g.affected_galleries.length > 0 && <span className="text-muted-foreground">galleries: {g.affected_galleries.join(", ")}</span>}</div>
                  <div className="font-mono text-[11px] text-foreground/90">KEEP {g.canonical.id} · {g.canonical.content_library} · {g.canonical.title || "(untitled)"} · {g.canonical.created_at?.slice(0, 19)} · {g.canonical.storage_path}</div>
                  {g.duplicates.map((d) => <div key={d.id} className="font-mono text-[11px] text-muted-foreground">TRASH {d.id} · {d.content_library} · {d.title || "(untitled)"} · {d.created_at?.slice(0, 19)} · {d.storage_path}</div>)}
                  <div className="text-[10px] text-muted-foreground mt-1">ids differ: {String(g.ids_differ)} · storage paths differ: {String(g.storage_paths_differ)} · hashes match: {String(g.hashes_match)} · orphan risk: {g.orphan_risk}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Tags & Search (owner taxonomy + human community suggestions + merge) ----------
const TagsPanel = () => {
  const { data: tags, loading, reload } = useFetch("/tags");
  const { data: suggestions, loading: sugLoading, reload: reloadSug } = useFetch("/admin/tags/suggestions");
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const merge = async () => {
    if (!from.trim() || !to.trim()) { toast.error("Enter both tags"); return; }
    try { await api.post("/admin/tags/merge", { from, to }); toast.success("Tags merged"); setFrom(""); setTo(""); reload(); }
    catch { toast.error("Failed"); }
  };
  const approve = async (tag) => {
    try { const r = await api.post("/admin/tags/approve", { tag }); toast.success(`"${tag}" is now official on ${r.data.media_updated} photos`); reloadSug(); reload(); }
    catch { toast.error("Failed to approve"); }
  };
  const dismiss = async (tag) => {
    try { await api.post("/admin/tags/dismiss", { tag }); toast.success(`Dismissed "${tag}"`); reloadSug(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Panel title="Tags & Search">
      <MasterRegistry />
      <div className="mb-7" data-testid="community-tag-suggestions">
        <div className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Community Tag Suggestions</div>
        <p className="text-xs text-muted-foreground mb-3">Tags added privately by 10+ different viewers. Approve to make one official — it gets stamped on every photo people tagged with it, visible to everyone.</p>
        {sugLoading ? <Loader2 className="w-5 h-5 animate-spin text-primary" aria-hidden="true" />
          : (suggestions?.length ? (
            <div className="space-y-2">
              {suggestions.map((s) => (
                <div key={s.tag} data-testid={`tag-suggestion-${s.tag}`} className="flex items-center justify-between gap-3 rounded-lg bg-secondary/40 border border-border px-3 py-2">
                  <div className="min-w-0">
                    <span className="text-sm font-medium">{s.tag}</span>
                    <span className="text-xs text-muted-foreground ml-2">{s.user_count} people · {s.media_count} photos</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button data-testid={`tag-approve-${s.tag}`} onClick={() => approve(s.tag)} size="sm" className="rounded-full h-8"><Check className="w-4 h-4 mr-1" aria-hidden="true" />Approve</Button>
                    <Button data-testid={`tag-dismiss-${s.tag}`} onClick={() => dismiss(s.tag)} size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground">Dismiss</Button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No suggestions yet — they appear once a tag is used by 10+ viewers.</p>)}
      </div>
      <div className="mb-7" data-testid="tag-merge-section">
        <div className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Merge tags</div>
        <div className="flex flex-wrap gap-2 items-end">
          <div><label className="text-xs text-muted-foreground">Merge tag</label><Input data-testid="tag-from" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="old tag" className="bg-secondary/50 border-border w-32 mt-1" /></div>
          <span className="pb-2">→</span>
          <div><label className="text-xs text-muted-foreground">into</label><Input data-testid="tag-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="new tag" className="bg-secondary/50 border-border w-32 mt-1" /></div>
          <Button data-testid="tag-merge" onClick={merge} size="sm" className="rounded-full"><Tag className="w-4 h-4 mr-1" aria-hidden="true" />Merge</Button>
        </div>
      </div>
      <div data-testid="official-tags-in-use">
        <div className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Official Tags In Use</div>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-primary" aria-hidden="true" />
          : (tags?.length ? <div className="flex flex-wrap gap-2">{tags.map((t) => <span key={t.tag} className="text-sm rounded-full bg-secondary/50 border border-border px-3 py-1">{t.tag} <span className="text-muted-foreground">{t.count}</span></span>)}</div>
            : <p className="text-sm text-muted-foreground">No tags yet — add tags when editing content.</p>)}
      </div>
    </Panel>
  );
};

// ---------- Galleries ----------
const GalleryAddPicker = ({ gallery, existingIds, onClose, onAdded }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get("/admin/media", { params: { status: "approved" } });
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtered = (items || []).filter((it) => !existingIds.has(it.id) &&
    (!q.trim() || (it.title || "").toLowerCase().includes(q.toLowerCase()) || (it.uploader_name || "").toLowerCase().includes(q.toLowerCase())));
  const addSelected = async () => {
    if (!sel.size) return;
    setSaving(true);
    try {
      const r = await api.post(`/admin/galleries/${gallery.id}/add`, { media_ids: Array.from(sel) });
      toast.success(`Added ${sel.size} — gallery now has ${r.data.count}`);
      onAdded?.();
      onClose();
    } catch { toast.error("Failed to add"); }
    setSaving(false);
  };
  return (
    <Dialog open={!!gallery} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-card border-border max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle>Add photos / videos to "{gallery?.name}"</DialogTitle></DialogHeader>
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="gallery-picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or creator…" className="pl-9 bg-secondary/50 border-border h-9" />
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          {!items ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto my-10" />
            : filtered.length === 0 ? <p className="text-sm text-muted-foreground py-10 text-center">No approved content available to add.</p>
            : <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {filtered.map((it) => {
                  const active = sel.has(it.id);
                  return (
                    <button key={it.id} data-testid={`gallery-picker-item-${it.id}`} onClick={() => toggle(it.id)}
                      className={`relative rounded-lg overflow-hidden border-2 text-left transition-all ${active ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary/40"}`}>
                      <div className="aspect-square bg-black">
                        {it.media_type === "video" ? <video src={videoUrl(it)} poster={posterUrl(it, 400)} muted className="w-full h-full object-cover" /> : <img src={thumbUrl(it.file_ref, 400)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-black/60 text-white/70"}`}>
                        {active ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </div>
                      <div className="p-1.5"><div className="text-xs font-medium truncate">{it.title}</div><div className="text-[10px] text-muted-foreground truncate">{it.uploader_name}</div></div>
                    </button>
                  );
                })}
              </div>}
        </div>
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-border mt-3">
          <span className="text-sm text-muted-foreground">{sel.size} selected</span>
          <Button data-testid="gallery-picker-add" onClick={addSelected} disabled={!sel.size || saving} className="rounded-full">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            Add {sel.size || ""} selected
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const GalleryItemsDialog = ({ gallery, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [picker, setPicker] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const load = useCallback(async () => { if (!gallery) return; const r = await api.get(`/admin/galleries/${gallery.id}`); setData(r.data); }, [gallery]);
  useEffect(() => { load(); }, [load]);
  const removeItem = async (mid) => { await api.post(`/admin/galleries/${gallery.id}/remove`, { media_id: mid }); load(); onChanged?.(); };
  const persistOrder = async (items) => {
    const prev = data.items;
    setData((d) => ({ ...d, items }));
    try {
      await api.patch(`/admin/galleries/${gallery.id}`, { media_ids: items.map((i) => i.id) });
      onChanged?.();
    } catch {
      setData((d) => ({ ...d, items: prev }));
      toast.error("Couldn't save the new order");
    }
  };
  const onDrop = async (to) => {
    if (dragIdx === null || dragIdx === to) { setDragIdx(null); setOverIdx(null); return; }
    const items = [...data.items];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(to, 0, moved);
    setDragIdx(null); setOverIdx(null);
    await persistOrder(items);
  };
  const setCover = async (item) => { await api.patch(`/admin/galleries/${gallery.id}`, { cover_url: fileUrl(item.file_ref) }); toast.success("Cover set"); onChanged?.(); };
  const existingIds = new Set((data?.items || []).map((i) => i.id));
  return (
    <Dialog open={!!gallery} onOpenChange={(o) => { if (!o && !picker) onClose(); }}>
      <DialogContent className="max-w-2xl bg-card border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Manage "{gallery?.name}" — {data?.items?.length || 0} items</DialogTitle></DialogHeader>
        <Button data-testid="gallery-open-picker" onClick={() => setPicker(true)} className="w-full rounded-full mb-1">
          <Plus className="w-4 h-4 mr-1.5" /> Add photos / videos
        </Button>
        {picker && <GalleryAddPicker gallery={gallery} existingIds={existingIds} onClose={() => setPicker(false)} onAdded={() => { load(); onChanged?.(); }} />}
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-primary" />
          : data.items.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No items yet. Tap <b>Add photos / videos</b> above to fill this gallery.</p>
          : <>
            <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5" /> Drag any item to reorder</p>
            <div className="space-y-2">
            {data.items.map((item, idx) => (
              <div key={item.id} data-testid={`gallery-item-${item.id}`}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
                onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
                onDrop={() => onDrop(idx)}
                className={`flex items-center gap-3 rounded-lg border p-2 cursor-grab active:cursor-grabbing transition-colors ${overIdx === idx && dragIdx !== null ? "border-primary bg-primary/5" : "border-border"} ${dragIdx === idx ? "opacity-40" : ""}`}>
                <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="font-mono text-xs text-muted-foreground w-5 text-center">{idx + 1}</span>
                <div className="w-16 h-11 rounded overflow-hidden bg-black shrink-0">{item.media_type === "video" ? <video src={videoUrl(item)} poster={posterUrl(item, 200)} muted className="w-full h-full object-cover" /> : <img src={thumbUrl(item.file_ref, 200)} alt="" draggable={false} className="w-full h-full object-cover" />}</div>
                <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{item.title}</div><div className="text-xs text-muted-foreground truncate">{item.uploader_name}</div></div>
                {item.media_type === "video"
                  ? <Button size="sm" variant="outline" disabled title="Videos can't be a cover" className="h-8 rounded-full border-border text-xs opacity-40">Cover</Button>
                  : <Button size="sm" variant="outline" data-testid={`gallery-setcover-${item.id}`} onClick={() => setCover(item)} className="h-8 rounded-full border-border text-xs">Cover</Button>}
                <Button size="icon" variant="outline" data-testid={`gallery-removeitem-${item.id}`} onClick={() => removeItem(item.id)} className="h-8 w-8 rounded-full border-border hover:text-destructive hover:border-destructive/50"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
            </div>
          </>}
      </DialogContent>
    </Dialog>
  );
};

const GALLERY_SUBS = [["manager", "Gallery Manager", Layers], ["smart", "Smart Galleries", Sparkles], ["organizer", "Auto Organizer", RefreshCw]];
const GalleriesPanel = ({ sub = "manager", setSub, initialEditId = null, onEditConsumed }) => (
  <div data-testid="galleries-panel">
    <div className="flex flex-wrap gap-2 mb-5" data-testid="galleries-subnav" role="tablist">
      {GALLERY_SUBS.map(([v, l, Icon]) => (
        <button key={v} type="button" role="tab" aria-selected={sub === v} data-testid={`galleries-sub-${v}`} onClick={() => setSub(v)}
          className={cn("inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm border transition-colors", sub === v ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
          <Icon className="w-3.5 h-3.5" aria-hidden="true" />{l}
        </button>
      ))}
    </div>
    {sub === "manager" && <GalleryManagerPanel />}
    {sub === "smart" && <Panel title="Smart Galleries"><SmartGalleriesPanel initialEditId={initialEditId} onEditConsumed={onEditConsumed} /></Panel>}
    {sub === "organizer" && <Panel title="Auto Organizer"><AutoOrganizerPanel /></Panel>}
  </div>
);

const GalleryManagerPanel = () => {
  const { data: galleries, loading, reload } = useFetch("/admin/galleries");
  const { data: smart } = useFetch("/collections/system");
  const { data: picTree, error: picErr, reload: reloadPic } = useFetch("/galleries/tree?media_type=image");
  const { data: vidTree, error: vidErr, reload: reloadVid } = useFetch("/galleries/tree?media_type=video");
  // Real representative covers, scoped to the Originals library (this manager works on owner content). Manual pick wins server-side.
  const { data: picCover } = useFetch("/galleries/library-cover?media_type=image&library=originals");
  const { data: vidCover } = useFetch("/galleries/library-cover?media_type=video&library=originals");
  const navigate = useNavigate();
  const [mediaType, setMediaType] = useState(null); // null = choose Pictures/Videos
  const [name, setName] = useState("");
  const [openCat, setOpenCat] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [manage, setManage] = useState(null);
  const create = async () => { if (!name.trim()) return; try { await api.post("/admin/galleries", { name }); toast.success("Gallery created"); setName(""); reload(); } catch { toast.error("Failed"); } };
  const toggle = async (g) => { await api.patch(`/admin/galleries/${g.id}`, { status: g.status === "live" ? "hidden" : "live" }); reload(); };
  const move = async (g, dir) => {
    const ids = (galleries || []).map((x) => x.id);
    const i = ids.indexOf(g.id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await Promise.all(ids.map((id, idx) => api.patch(`/admin/galleries/${id}`, { order: idx })));
    reload();
  };
  const del = async (g) => { if (!window.confirm(`Delete gallery "${g.name}"?`)) return; await api.delete(`/admin/galleries/${g.id}`); reload(); };
  const saveName = async (g) => { if (editName.trim() && editName !== g.name) { await api.patch(`/admin/galleries/${g.id}`, { name: editName }); reload(); } setEditId(null); };
  return (
    <Panel title="Gallery Manager" action={<div className="flex gap-2"><Input data-testid="gallery-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New gallery name" className="bg-secondary/50 border-border h-9 w-44" /><Button data-testid="gallery-create" onClick={create} size="sm" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Create</Button></div>}>
      <GalleryItemsDialog gallery={manage} onClose={() => setManage(null)} onChanged={reload} />
      {mediaType === null ? (
        <div className="mb-7" data-testid="media-library-picker">
          <div className="text-xs uppercase tracking-widest text-primary mb-3 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Libraries</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["image", "Pictures", ImageIcon, picCover], ["video", "Videos", Video, vidCover]].map(([mt, label, Icon, cov]) => {
              const c = cov?.cover;
              const src = c ? (c.media_type === "video" ? c.poster : thumbUrl(c.file_ref, 800)) : null;
              return (
                <button key={mt} data-testid={`media-lib-${mt}`} onClick={() => setMediaType(mt)}
                  className="relative overflow-hidden flex items-center gap-4 rounded-xl border border-primary/40 bg-primary/10 hover:border-primary transition-colors p-5 text-left min-h-[106px]">
                  {src ? (
                    <>
                      <div className="absolute inset-0" data-testid={`media-lib-cover-${mt}`}><SmartCover src={src} frame={c.frame} aspect={4.2} /></div>
                      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/25" />
                    </>
                  ) : null}
                  <div className={`relative w-16 h-16 rounded-lg flex items-center justify-center shrink-0 ${src ? "bg-black/40 backdrop-blur border border-white/10" : "bg-primary/20"}`} data-testid={`media-lib-icon-${mt}`}><Icon className={src ? "w-5 h-5 text-primary" : "w-8 h-8 text-primary"} /></div>
                  <div className="relative flex-1 min-w-0">
                    <div className="text-2xl font-heading text-white drop-shadow">{label}</div>
                    <div className="text-sm text-white/75 drop-shadow">Inbox, No Location & every category — {label.toLowerCase()} only{cov?.manual ? " · cover chosen manually" : ""}</div>
                  </div>
                  <ChevronRight className="relative w-6 h-6 text-primary" />
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="mb-7" data-testid="media-library-view">
          <button data-testid="gallery-lib-back" onClick={() => setMediaType(null)} className="flex items-center gap-1.5 text-sm text-primary hover:underline mb-3"><ChevronRight className="w-4 h-4 rotate-180" /> Libraries</button>
          <div className="text-xl font-heading mb-3 flex items-center gap-2">{mediaType === "video" ? <Video className="w-5 h-5 text-primary" /> : <ImageIcon className="w-5 h-5 text-primary" />}{mediaType === "video" ? "Videos" : "Pictures"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {(smart || []).filter((g) => g.system_key === "inbox").map((inbox) => (
              <button key={inbox.id} data-testid="smart-gallery-inbox" onClick={() => navigate(`/collection/${inbox.id}?type=${mediaType}`)}
                className="flex items-center gap-4 rounded-xl border border-primary/40 bg-primary/10 hover:bg-primary/20 transition-colors p-4 text-left">
                <div className="w-16 h-16 rounded-lg bg-primary/20 flex items-center justify-center shrink-0"><Layers className="w-8 h-8 text-primary" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl font-heading">Inbox — needs review</div>
                  <div className="text-sm text-muted-foreground">Items still needing a title, description, owner tags, or location. Fix it, Save, and it leaves on its own.</div>
                </div>
              </button>
            ))}
            {(smart || []).filter((g) => g.system_key === "no-location").map((noloc) => (
              <button key={noloc.id} data-testid="smart-gallery-no-location" onClick={() => navigate(`/collection/${noloc.id}?type=${mediaType}`)}
                className="flex items-center gap-4 rounded-xl border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 transition-colors p-4 text-left">
                <div className="w-16 h-16 rounded-lg bg-amber-400/20 flex items-center justify-center shrink-0"><MapPin className="w-8 h-8 text-amber-400" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl font-heading">No Location</div>
                  <div className="text-sm text-muted-foreground">Open one, add a city to put it on the map.</div>
                </div>
              </button>
            ))}
          </div>
          <div className="text-xs uppercase tracking-widest text-primary mb-3 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Galleries — by category</div>
          {(() => {
            const activeTree = mediaType === "video" ? vidTree : picTree;
            const activeErr = mediaType === "video" ? vidErr : picErr;
            const activeReload = mediaType === "video" ? reloadVid : reloadPic;
            if (activeErr && !activeTree) return (
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4" data-testid="galleries-load-error">
                <span className="text-sm text-destructive">Couldn't load galleries. Please try again.</span>
                <Button size="sm" variant="outline" data-testid="galleries-retry" onClick={activeReload} className="h-8 rounded-full border-border">Retry</Button>
              </div>
            );
            if (!activeTree) return <div className="mb-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
            if ((activeTree.tree || []).length === 0) return <p className="text-sm text-muted-foreground mb-4">No {mediaType === "video" ? "video" : "photo"} galleries yet.</p>;
            return null;
          })()}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {((mediaType === "video" ? vidTree : picTree)?.tree || []).map((cat) => (
              <button key={cat.category} data-testid={`admin-cat-${cat.category}`} onClick={() => navigate(`/galleries?cat=${encodeURIComponent(cat.category)}&type=${mediaType}`)}
                className="text-left rounded-xl border border-border bg-card/40 overflow-hidden hover:border-primary/60 hover:-translate-y-0.5 transition-all">
                <div className="relative aspect-square bg-black">
                  {cat.cover && cat.cover_type !== "video" ? <SmartCover src={`${fileUrl(cat.cover)}?w=600`} frame={cat.cover_frame} aspect={1} />
                    : cat.cover_type === "video" && cat.cover_poster ? <SmartCover src={cat.cover_poster} frame={cat.cover_frame} aspect={1} />
                    : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary to-black"><Layers className="w-7 h-7 text-muted-foreground" /></div>}
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/80 to-transparent" />
                </div>
                <div className="p-2.5">
                  <div className="text-sm font-heading">{cat.category}</div>
                  <div className="text-[11px] text-muted-foreground">{cat.tags.length} galleries · {cat.count}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-primary mb-3 mt-8 flex items-center gap-1.5 border-t border-border pt-6"><Layers className="w-3.5 h-3.5" /> Custom curated galleries {galleries?.length ? `(${galleries.length})` : ""}</div>
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
        : (galleries?.length ? (
          <div className="divide-y divide-border">
            {galleries.map((g) => (
              <div key={g.id} data-testid={`gallery-${g.id}`} className="flex items-center gap-3 py-3">
                <div className="w-14 h-10 rounded bg-secondary overflow-hidden shrink-0" data-testid={`gallery-cover-${g.id}`}>
                  {g.cover ? <SmartCover src={g.cover.media_type === "video" ? (g.cover.poster || `${fileUrl(g.cover.file_ref)}?poster=1&w=200`) : thumbUrl(g.cover.file_ref, 200)} frame={g.cover.frame} aspect={1.4} className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="w-4 h-4" aria-hidden="true" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  {editId === g.id
                    ? <input autoFocus data-testid={`gallery-rename-input-${g.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} onBlur={() => saveName(g)} onKeyDown={(e) => e.key === "Enter" && saveName(g)} className="bg-secondary/50 border border-border rounded px-2 py-1 text-sm w-full" />
                    : <button data-testid={`gallery-rename-${g.id}`} onClick={() => { setEditId(g.id); setEditName(g.name); }} className="font-medium text-sm hover:text-primary text-left flex items-center gap-1">{g.name} <Pencil className="w-3 h-3 opacity-50" /></button>}
                  <div className="text-xs text-muted-foreground">{g.count} items · order {g.order}</div>
                </div>
                <span className={cn("text-[10px] uppercase font-bold px-2 py-0.5 rounded", g.status === "live" ? "bg-emerald-500/20 text-emerald-400" : "bg-secondary text-muted-foreground")}>{g.status}</span>
                <Button size="sm" variant="outline" data-testid={`gallery-manage-${g.id}`} onClick={() => setManage(g)} className="h-8 rounded-full border-border text-xs">Manage items</Button>
                <Button size="icon" variant="outline" data-testid={`gallery-up-${g.id}`} onClick={() => move(g, -1)} className="h-8 w-8 rounded-full border-border"><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="outline" data-testid={`gallery-down-${g.id}`} onClick={() => move(g, 1)} className="h-8 w-8 rounded-full border-border"><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="outline" data-testid={`gallery-toggle-${g.id}`} onClick={() => toggle(g)} className="h-8 w-8 rounded-full border-border"><Eye className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="outline" data-testid={`gallery-delete-${g.id}`} onClick={() => del(g)} className="h-8 w-8 rounded-full border-border hover:text-destructive hover:border-destructive/50"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground" data-testid="no-custom-galleries">Your photos & videos are already sorted into the <span className="text-primary">Pictures</span> and <span className="text-primary">Videos</span> libraries above. Create a custom gallery only if you want a hand picked set — use the box top right, then add posts from the Content tab.</p>)}
      <GallerySourcePanel />
    </Panel>
  );
};

// ---------- Homepage Control ----------
const HomepagePanel = () => {
  const [sections, setSections] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setSections(r.data.homepage_sections)); }, []);
  const save = async (next) => { setSections(next); try { await api.put("/admin/settings", { homepage_sections: next }); } catch {} };
  const toggle = (i) => save(sections.map((s, idx) => idx === i ? { ...s, enabled: !s.enabled } : s));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= sections.length) return; const next = [...sections]; [next[i], next[j]] = [next[j], next[i]]; save(next); };
  if (!sections) return <Loader2 className="w-6 h-6 animate-spin text-primary" />;
  return (
    <Panel title="Homepage Control (order & visibility)">
      <div className="divide-y divide-border">
        {sections.map((s, i) => (
          <div key={s.id} data-testid={`hp-${s.id}`} className="flex items-center gap-3 py-3">
            <span className="text-muted-foreground font-mono text-xs w-5">{i + 1}</span>
            <span className="flex-1 text-sm">{s.label}</span>
            <Button size="icon" variant="outline" data-testid={`hp-up-${s.id}`} onClick={() => move(i, -1)} className="h-8 w-8 rounded-full border-border"><ArrowUp className="w-3.5 h-3.5" /></Button>
            <Button size="icon" variant="outline" data-testid={`hp-down-${s.id}`} onClick={() => move(i, 1)} className="h-8 w-8 rounded-full border-border"><ArrowDown className="w-3.5 h-3.5" /></Button>
            <button data-testid={`hp-toggle-${s.id}`} onClick={() => toggle(i)} className={cn("w-11 h-6 rounded-full transition-colors relative", s.enabled ? "bg-primary" : "bg-secondary")}>
              <span className={cn("absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all", s.enabled ? "left-5" : "left-0.5")} />
            </button>
          </div>
        ))}
      </div>
    </Panel>
  );
};

// ---------- Banners ----------
const BannersPanel = () => {
  const { data: banners, loading, reload } = useFetch("/admin/banners");
  const [form, setForm] = useState({ title: "", image_url: "", link: "" });
  const create = async () => { if (!form.image_url.trim()) { toast.error("Image URL required"); return; } try { await api.post("/admin/banners", form); toast.success("Banner added"); setForm({ title: "", image_url: "", link: "" }); reload(); } catch { toast.error("Failed"); } };
  const toggle = async (b) => { await api.patch(`/admin/banners/${b.id}`, { active: !b.active }); reload(); };
  const del = async (b) => { await api.delete(`/admin/banners/${b.id}`); reload(); };
  return (
    <div className="space-y-4">
      <Panel title="Add Banner">
        <div className="grid sm:grid-cols-3 gap-2">
          <Input data-testid="banner-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className="bg-secondary/50 border-border" />
          <Input data-testid="banner-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL" className="bg-secondary/50 border-border" />
          <Input data-testid="banner-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Link (optional)" className="bg-secondary/50 border-border" />
        </div>
        <Button data-testid="banner-create" onClick={create} size="sm" className="rounded-full mt-3"><Plus className="w-4 h-4 mr-1" />Add Banner</Button>
      </Panel>
      <Panel title="Banners">
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
          : (banners?.length ? <div className="space-y-2">{banners.map((b) => (
            <div key={b.id} data-testid={`banner-${b.id}`} className="flex items-center gap-3">
              <img src={b.image_url} alt="" className="w-16 h-10 object-cover rounded bg-black" />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{b.title || "Untitled"}</div><div className="text-xs text-muted-foreground truncate">{b.link || "—"}</div></div>
              <button data-testid={`banner-toggle-${b.id}`} onClick={() => toggle(b)} className={cn("w-11 h-6 rounded-full relative", b.active ? "bg-primary" : "bg-secondary")}><span className={cn("absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all", b.active ? "left-5" : "left-0.5")} /></button>
              <Button size="icon" variant="outline" data-testid={`banner-delete-${b.id}`} onClick={() => del(b)} className="h-8 w-8 rounded-full border-border hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          ))}</div> : <p className="text-sm text-muted-foreground">No banners yet.</p>)}
      </Panel>
    </div>
  );
};

// ---------- Reports / Moderation ----------
const ReportsPanel = ({ onOpen, onChange }) => {
  const { data: reports, loading, reload } = useFetch("/admin/reports?status=open");
  const resolve = async (r) => { await api.post(`/admin/reports/${r.id}/resolve`); toast.success("Resolved"); reload(); onChange?.(); };
  return (
    <Panel title="Moderation & Safety — Reported Content">
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
        : (reports?.length ? <div className="divide-y divide-border">{reports.map((r) => (
          <div key={r.id} data-testid={`report-${r.id}`} className="flex items-center gap-3 py-3">
            <Flag className="w-4 h-4 text-destructive shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{r.target_type} · {r.label || r.target_id}</div>
              <div className="text-xs text-muted-foreground">Reason: {r.reason} · by {r.reporter_name}</div>
            </div>
            {r.target_type === "media" && <Button size="sm" variant="outline" onClick={() => onOpen(r.target_id)} className="rounded-full h-8 border-border"><Eye className="w-3.5 h-3.5 mr-1" />View</Button>}
            <Button size="sm" data-testid={`resolve-${r.id}`} onClick={() => resolve(r)} className="rounded-full h-8"><Check className="w-3.5 h-3.5 mr-1" />Resolve</Button>
          </div>
        ))}</div> : <p className="text-sm text-muted-foreground">No open reports. 🎉 Use Users → Boot off and Content → Delete for direct action.</p>)}
    </Panel>
  );
};

// ---------- Comments admin ----------
const CommentsPanel = ({ onChange }) => {
  const { data: comments, loading, reload } = useFetch("/admin/comments");
  const del = async (c) => { await api.delete(`/comments/${c.id}`); toast.success("Deleted"); reload(); onChange?.(); };
  return (
    <Panel title="Recent Comments">
      {loading ? <Loader2 className="w-5 h-5 animate-spin text-primary" />
        : (comments?.length ? <div className="divide-y divide-border">{comments.map((c) => (
          <div key={c.id} data-testid={`comment-${c.id}`} className="flex items-start gap-3 py-3">
            <Avatar className="w-8 h-8 border border-border"><AvatarImage src={resolveAvatar(c.user_avatar)} /><AvatarFallback className="bg-secondary text-[10px]">{c.user_name?.[0]}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0"><div className="text-sm"><span className="font-medium">{c.user_name}</span> <span className="text-muted-foreground text-xs">on {c.media_title}</span></div><p className="text-sm text-foreground/90 break-words">{c.text}</p></div>
            <Button size="icon" variant="outline" data-testid={`comment-delete-${c.id}`} onClick={() => del(c)} className="h-8 w-8 rounded-full border-border hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}</div> : <p className="text-sm text-muted-foreground">No comments yet.</p>)}
    </Panel>
  );
};

const ATTENTION_LABELS = { ai_incomplete: "Incomplete AI Jobs", missing_metadata: "Missing title / description", duplicates: "Duplicate uploads", tag_integrity: "Tag verification mismatch (saved tags ≠ canonical tags)" };

const AiJobCard = ({ item, onRetried }) => {
  const [busy, setBusy] = useState(null);
  const job = item.ai_job || {};
  const retry = async (stage) => {
    setBusy(stage);
    try {
      const r = await api.post(`/admin/media/${item.id}/ai-retry`, { stage });
      toast.success(r.data.still_flagged ? "Retry finished but the job is still incomplete — see error" : "AI job completed — owner title/description/tags untouched");
      onRetried?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    setBusy(null);
  };
  return (
    <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] space-y-0.5" data-testid={`ai-job-${item.id}`}>
      <div className="text-amber-300 font-semibold uppercase tracking-widest text-[10px]">AI job incomplete</div>
      <div>Media: <span className="text-foreground">{item.title || "Untitled"}</span> · ID <span className="font-mono">{item.id}</span></div>
      <div>Library: <span className="text-foreground">{item.content_library || "originals"}</span> · Stage: <span className="text-foreground">{job.stage || "AI tagging"}</span> · Status: <span className="text-foreground">{job.status || "unknown"}</span></div>
      <div>Last attempt: {job.last_attempt ? new Date(job.last_attempt).toLocaleString() : "—"}{job.error ? <> · Error: <span className="text-red-300">{job.error}</span></> : null}</div>
      <div className="text-muted-foreground">Retry never overwrites owner-approved title, description, manual or rejected tags.</div>
      <div className="flex gap-1.5 pt-1">
        <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" data-testid={`ai-retry-tagging-${item.id}`} disabled={!!busy} onClick={() => retry("tagging")}>{busy === "tagging" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Retry Tagging"}</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-full text-xs" data-testid={`ai-retry-caption-${item.id}`} disabled={!!busy} onClick={() => retry("caption")}>{busy === "caption" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Retry Caption"}</Button>
      </div>
    </div>
  );
};

const ContentLibrary = ({ onOpen, onChange, initialStatus = "all", initialQ = "", attention = null, onClearAttention, showInboxTools = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(initialStatus);
  const [type, setType] = useState("all");
  const [source, setSource] = useState("all");
  const [library, setLibrary] = useState("all");
  const [namedSubject, setNamedSubject] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [q, setQ] = useState(initialQ);
  const [galleries, setGalleries] = useState([]);
  const [selecting, setSelecting] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkTag, setBulkTag] = useState("");
  const [bulkTagging, setBulkTagging] = useState(false);

  const bulkTags = async (op) => {
    const tag = bulkTag.trim().toLowerCase();
    if (!sel.size || !tag) return;
    setBulkTagging(true);
    try {
      await api.post("/admin/media/bulk_tags", { ids: Array.from(sel), add: op === "add" ? [tag] : [], remove: op === "remove" ? [tag] : [] });
      toast.success(`${op === "add" ? "Added" : "Removed"} ${tag} on ${sel.size}`);
      load();
    } catch { toast.error("Failed"); }
    setBulkTagging(false);
  };
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exitSelect = () => { setSelecting(false); setSel(new Set()); };
  const bulkDelete = async () => {
    if (!sel.size) return;
    if (!window.confirm(`Delete ${sel.size} post${sel.size > 1 ? "s" : ""}? This hides them from the site.`)) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/admin/media/bulk-delete", { ids: Array.from(sel) });
      toast.success(`Deleted ${r.data.deleted} post${r.data.deleted !== 1 ? "s" : ""}`);
      exitSelect(); load(); onChange?.();
    } catch { toast.error("Bulk delete failed"); }
    setBulkDeleting(false);
  };
  const bulkAddToGallery = async (gid) => {
    if (!sel.size) return;
    try {
      const r = await api.post(`/admin/galleries/${gid}/add`, { media_ids: Array.from(sel) });
      toast.success(`Added ${sel.size} to gallery — now ${r.data.count} items`);
      exitSelect();
    } catch { toast.error("Failed to add"); }
  };

  useEffect(() => { api.get("/admin/galleries").then((r) => setGalleries(r.data)).catch(() => {}); }, []);
  const addToGallery = async (gid, mediaId) => {
    try { const r = await api.post(`/admin/galleries/${gid}/add`, { media_ids: [mediaId] }); toast.success(`Added to gallery (${r.data.count})`); }
    catch { toast.error("Failed to add"); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status };
      if (type !== "all") params.type = type === "photos" ? "image" : "video";
      if (source !== "all") params.source = source;
      if (library !== "all") params.library = library;
      if (namedSubject) params.named_subject = namedSubject;
      if (q.trim()) params.q = q.trim();
      if (attention) { params.attention = attention; params.status = "all"; }
      const res = await api.get("/admin/media", { params });
      setItems(res.data);
    } finally { setLoading(false); }
  }, [status, type, source, library, namedSubject, q, attention]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // Named-subject chips for celebrity/pornstar libraries (auto-populated).
  useEffect(() => {
    setNamedSubject(null);
    if (["celebrities", "pornstars", "web_finds"].includes(library)) {
      api.get("/library/subjects", { params: { library } }).then((r) => setSubjects(r.data.subjects || [])).catch(() => setSubjects([]));
    } else { setSubjects([]); }
  }, [library]);

  const moderate = async (id, action) => {
    try { await api.post(`/admin/media/${id}/moderate`, { action }); toast.success(action === "approve" ? "Published" : "Rejected"); load(); onChange?.(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this post from the galleries? (it will be hidden from the site)")) return;
    try { await api.delete(`/media/${id}`); toast.success("Deleted"); load(); onChange?.(); }
    catch { toast.error("Failed"); }
  };
  const toggleFeature = async (item) => {
    try { await api.post(`/admin/media/${item.id}/feature`, { featured: !item.featured }); toast.success(item.featured ? "Unfeatured" : "Featured on homepage"); load(); }
    catch { toast.error("Failed"); }
  };
  const [resettingInbox, setResettingInbox] = useState(false);
  const resetInbox = async () => {
    if (!window.confirm("Put ALL photos and videos back into the Inbox to review? This just resets their review status so you can re-tag with the new system. Nothing is deleted and no tags are lost.")) return;
    setResettingInbox(true);
    try { const r = await api.post("/admin/inbox/reset-all", {}); toast.success(`${r.data.moved} item${r.data.moved === 1 ? "" : "s"} sent back to the Inbox`); load(); onChange?.(); }
    catch { toast.error("Couldn't reset the Inbox"); }
    setResettingInbox(false);
  };

  const Pill = ({ v, cur, set, children, testid }) => (
    <button data-testid={testid} onClick={() => set(v)}
      className={cn("px-3 py-1.5 rounded-full text-sm border transition-colors whitespace-nowrap",
        cur === v ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground")}>{children}</button>
  );

  return (
    <div>
      {attention && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-[#d9a24a]/50 bg-[#d9a24a]/10 px-4 py-2.5" data-testid="attention-banner">
          <div className="flex items-center gap-2 text-sm"><AlertTriangle className="w-4 h-4 text-[#d9a24a]" /><span className="text-[#d9a24a] font-semibold">Needs Attention:</span> {ATTENTION_LABELS[attention] || attention} · <span data-testid="attention-count">{loading ? "loading…" : attention === "duplicates" ? `${new Set(items.map((i) => i.dup_group)).size} groups · ${items.length} items` : `${items.length} item${items.length === 1 ? "" : "s"}`}</span></div>
          <button data-testid="attention-clear" onClick={onClearAttention} className="text-xs text-[#d9a24a] hover:text-white inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Clear filter</button>
        </div>
      )}
      <div className="relative mb-4">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input data-testid="content-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles & descriptions…"
          className="w-full rounded-full bg-card border border-border pl-10 pr-4 py-2.5 text-sm outline-none focus:border-primary/50" />
      </div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid="content-library-nav">
        {[["all", "All Content"], ["originals", "Originals"], ["celebrities", "Celebrities"], ["pornstars", "Pornstars"], ["web_finds", "Best of the Web"], ["community", "Community"]].map(([v, l]) => <Pill key={v} testid={`content-lib-${v}`} v={v} cur={library} set={setLibrary}>{l}</Pill>)}
      </div>
      {(library === "celebrities" || library === "pornstars" || (library === "web_finds" && subjects.length > 0)) && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="content-named-subjects">
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground mr-1">Named subjects</span>
          <button onClick={() => setNamedSubject(null)} data-testid="named-subject-all"
            className={cn("px-3 py-1 rounded-full text-xs border transition-colors", !namedSubject ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground")}>All</button>
          {subjects.length === 0 ? <span className="text-xs text-muted-foreground">No named subjects yet — upload or Move to Library to add one.</span>
            : subjects.map((s) => (
              <button key={s.name} data-testid={`named-subject-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => setNamedSubject(s.name)}
                className={cn("px-3 py-1 rounded-full text-xs border transition-colors", namedSubject === s.name ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground")}>
                {s.name} <span className="opacity-60">{s.count}</span>
              </button>
            ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="content-review-controls">
        {["all", "pending", "approved", "rejected", "untagged"].map((s) => <Pill key={s} testid={`content-status-${s}`} v={s} cur={status} set={setStatus}><span className="capitalize">{s === "pending" ? "Community queue" : s === "untagged" ? "No owner tags yet" : s}</span></Pill>)}
        {showInboxTools && (
          <>
            <span className="w-px h-6 bg-border mx-1" />
            <Button data-testid="reset-inbox-all" onClick={resetInbox} disabled={resettingInbox} size="sm" variant="outline" className="rounded-full border-border h-8" title="Resets every item's review status so it shows in the Inbox again. Nothing is deleted and no tags are lost.">
              {resettingInbox ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" /> : <Inbox className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />} Send all to Inbox
            </Button>
            <span className="text-[11px] text-muted-foreground" data-testid="reset-inbox-note">Resets review status only — nothing is deleted and existing tags stay.</span>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {[["all", "All"], ["photos", "Photos"], ["videos", "Videos"]].map(([v, l]) => <Pill key={v} v={v} cur={type} set={setType}>{l}</Pill>)}
        <span className="w-px h-6 bg-border mx-1" />
        {[["all", "All sources"], ["barefootage", "Official"], ["community", "Community"]].map(([v, l]) => <Pill key={v} v={v} cur={source} set={setSource}>{l}</Pill>)}
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {!selecting ? (
          <Button data-testid="content-select-mode" size="sm" variant="outline" onClick={() => setSelecting(true)} className="rounded-full border-border h-8"><CheckSquare className="w-3.5 h-3.5 mr-1.5" /> Select</Button>
        ) : (
          <>
            <span className="text-sm text-muted-foreground" data-testid="content-sel-count">{sel.size} selected</span>
            <Button data-testid="content-select-all" size="sm" variant="outline" onClick={() => setSel(new Set(items.map((i) => i.id)))} className="rounded-full border-border h-8">Select all ({items.length})</Button>
            {galleries.length > 0 && (
              <select data-testid="content-bulk-gallery" defaultValue="" onChange={(e) => { if (e.target.value) { bulkAddToGallery(e.target.value); e.target.value = ""; } }}
                disabled={!sel.size} className="rounded-full bg-card border border-border h-8 px-3 text-sm outline-none focus:border-primary disabled:opacity-40">
                <option value="">+ Add {sel.size || ""} to gallery…</option>
                {galleries.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
            <Button data-testid="content-bulk-delete" size="sm" onClick={bulkDelete} disabled={!sel.size || bulkDeleting} className="rounded-full h-8 bg-destructive hover:bg-destructive/90 text-white">
              {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />} Delete {sel.size || ""}
            </Button>
            <span className="w-px h-6 bg-border mx-1" />
            <input data-testid="content-bulk-tag-input" value={bulkTag} onChange={(e) => setBulkTag(e.target.value)}
              placeholder="tag…" className="rounded-full bg-card border border-border h-8 px-3 text-sm w-28 outline-none focus:border-primary" />
            <Button data-testid="content-bulk-tag-add" size="sm" variant="outline" onClick={() => bulkTags("add")} disabled={!sel.size || !bulkTag.trim() || bulkTagging} className="rounded-full border-border h-8"><Tag className="w-3.5 h-3.5 mr-1" /> Add tag</Button>
            <Button data-testid="content-bulk-tag-remove" size="sm" variant="outline" onClick={() => bulkTags("remove")} disabled={!sel.size || !bulkTag.trim() || bulkTagging} className="rounded-full border-border h-8">Remove tag</Button>
            <Button data-testid="content-select-cancel" size="sm" variant="outline" onClick={exitSelect} className="rounded-full border-border h-8">Cancel</Button>
          </>
        )}
      </div>

      {loading ? <div className="h-48 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        : items.length === 0 ? <div className="h-40 flex items-center justify-center text-muted-foreground border border-dashed border-border rounded-xl">No content.</div>
        : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((item) => (
              <div key={item.id} data-testid={`admin-item-${item.id}`} className={cn("rounded-xl border bg-card overflow-hidden transition-colors", selecting && sel.has(item.id) ? "border-primary ring-2 ring-primary/40" : "border-border")}>
                <div className="relative aspect-video bg-black cursor-pointer" onClick={() => selecting ? toggleSel(item.id) : onOpen(item.id, items)}>
                  {item.media_type === "video"
                    ? <video src={videoUrl(item)} poster={posterUrl(item, 400)} muted className="w-full h-full object-cover" />
                    : <img src={thumbUrl(item.file_ref, 400)} alt={item.title} className="w-full h-full object-cover" />}
                  {selecting && (
                    <div data-testid={`select-${item.id}`} className={cn("absolute inset-0 flex items-start justify-end p-2 bg-black/20")}>
                      <span className={cn("w-7 h-7 rounded-md border-2 flex items-center justify-center", sel.has(item.id) ? "bg-primary border-primary text-primary-foreground" : "bg-black/50 border-white/70")}>
                        {sel.has(item.id) && <Check className="w-4 h-4" />}
                      </span>
                    </div>
                  )}
                  <span className={cn("absolute top-2 left-2 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded",
                    item.status === "approved" ? "bg-emerald-500/80" : item.status === "pending" ? "bg-primary/80 text-primary-foreground" : "bg-destructive/80")}>{item.status}</span>
                  <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-black/70 flex items-center gap-1">
                    {item.media_type === "video" ? <><Video className="w-3 h-3" /> Video</> : <><ImageIcon className="w-3 h-3" /> Photo</>}</span>
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-medium text-sm truncate">{item.title}</h3>
                    <span className={cn("text-[10px] uppercase font-bold shrink-0", item.is_official ? "text-primary" : "text-emerald-400")}>{item.is_official ? "Official" : "Community"}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">by {item.uploader_name} · ⭐ {item.rating_count > 0 ? item.avg_rating : "—"} · {item.rating_count} votes</div>
                  {attention === "missing_metadata" && item.missing && <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-300" data-testid={`missing-${item.id}`}>{item.missing === "both" ? "Missing title & description" : item.missing === "title" ? "Missing title" : "Missing description"}</div>}
                  {attention === "duplicates" && item.dup_group && <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-300" data-testid={`dup-${item.id}`}>Duplicate group {item.dup_group}</div>}
                  {attention === "ai_incomplete" && <AiJobCard item={item} onRetried={() => { load(); onChange?.(); }} />}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {item.status !== "approved" && <Button size="sm" data-testid={`approve-${item.id}`} onClick={() => moderate(item.id, "approve")} className="flex-1 rounded-full h-8"><Check className="w-3.5 h-3.5 mr-1" />Publish</Button>}
                    {item.status === "pending" && <Button size="sm" variant="outline" data-testid={`reject-${item.id}`} onClick={() => moderate(item.id, "reject")} className="flex-1 rounded-full h-8 border-border"><X className="w-3.5 h-3.5 mr-1" />Reject</Button>}
                    <Button size="sm" variant="outline" data-testid={`feature-${item.id}`} title={item.featured ? "Unfeature" : "Feature on homepage"} aria-label={item.featured ? "Unfeature" : "Feature on homepage"} onClick={() => toggleFeature(item)} className={cn("rounded-full h-8 border-border", item.featured && "text-primary border-primary/50")}><Star className={cn("w-3.5 h-3.5", item.featured && "fill-primary")} /></Button>
                    <Button size="sm" variant="outline" data-testid={`edit-${item.id}`} onClick={() => onOpen(item.id, items)} title="Edit in the media editor" aria-label="Edit in the media editor" className="rounded-full h-8 border-border"><Pencil className="w-3.5 h-3.5" aria-hidden="true" /></Button>
                    <Button size="sm" variant="outline" data-testid={`view-${item.id}`} onClick={() => onOpen(item.id, items)} className="rounded-full h-8 border-border"><Eye className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" data-testid={`delete-${item.id}`} onClick={() => remove(item.id)} className="rounded-full h-8 border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                  {galleries.length > 0 && (
                    <select data-testid={`add-gallery-${item.id}`} defaultValue="" onChange={(e) => { if (e.target.value) { addToGallery(e.target.value, item.id); e.target.value = ""; } }}
                      className="mt-2 w-full bg-secondary/40 border border-border rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-primary text-muted-foreground">
                      <option value="">+ Add to gallery…</option>
                      {galleries.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

const BulkUpload = ({ onDone }) => {
  const inputRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [library, setLibrary] = useState("originals");
  const [subjectType, setSubjectType] = useState("");   // owner-selected Subject Type — NEVER inferred from the library ("" = Select subject type)
  const [named, setNamed] = useState("");
  const [source, setSource] = useState({ source_url: "", source_name: "", source_credit: "", source_notes: "" });
  const needsName = library === "celebrities" || library === "pornstars";
  const isWeb = library === "web_finds";

  const add = (fileList) => {
    const arr = Array.from(fileList || []).filter((f) => f.type.startsWith("image") || f.type.startsWith("video"));
    setQueue((q) => {
      const start = q.length;
      const next = [...q, ...arr.map((f) => ({ file: f, title: f.name.replace(/\.[^.]+$/, ""), status: "queued", traceId: newTraceId(),   // stable upload identity across retries
        pf: f.type.startsWith("video") ? { loading: true } : null }))];
      // DIAGNOSTIC-ONLY: inspect each video in the browser and attach a preflight result. Never blocks upload.
      arr.forEach((f, k) => {
        if (!f.type.startsWith("video")) return;
        const idx = start + k;
        preflightVideo(f).then((pf) => setQueue((qq) => qq.map((x, i) => i === idx ? { ...x, pf } : x)))
          .catch(() => setQueue((qq) => qq.map((x, i) => i === idx ? { ...x, pf: { status: "unknown", label: "? Codec unknown — upload pipeline will inspect" } } : x)));
      });
      return next;
    });
  };

  const pfColor = (s) => s === "hevc" ? "text-amber-400" : s === "unknown" ? "text-muted-foreground"
    : s === "h264mov" ? "text-sky-400" : "text-emerald-400";

  const uploadOne = async (item, i) => {
    const f = item.file;
    const fields = { title: item.title || f.name, description: "", duration: "0", content_library: library, named_subjects: (needsName || isWeb) ? named : "", tags: subjectType };   // tags = explicit owner selection only
    if (isWeb) Object.assign(fields, source);
    await uploadMediaFile(f, fields, (pct) => setQueue((q) => q.map((x, idx) => idx === i ? { ...x, pct } : x)), { traceId: item.traceId });
  };

  const start = async () => {
    if (needsName && !named.trim()) { toast.error(`Enter a ${library === "pornstars" ? "performer" : "celebrity"} name before publishing`); return; }
    setUploading(true);
    let failed = 0, dup = 0;
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].status === "done" || queue[i].status === "dup") continue;
      setQueue((q) => q.map((x, idx) => idx === i ? { ...x, status: "uploading", pct: 0 } : x));
      try {
        await uploadOne(queue[i], i);
        setQueue((q) => q.map((x, idx) => idx === i ? { ...x, status: "done" } : x));
      } catch (e) {
        if (e?.status === 409 || e?.response?.status === 409) {
          dup += 1;
          setQueue((q) => q.map((x, idx) => idx === i ? { ...x, status: "dup" } : x));
        } else {
          failed += 1;
          setQueue((q) => q.map((x, idx) => idx === i ? { ...x, status: "error", error: describeUploadError(e) } : x));
          toast.error(describeUploadError(e), { duration: 12000 });
        }
      }
    }
    setUploading(false);
    if (failed > 0) toast.error(`${failed} file(s) failed to upload`);
    else if (dup > 0) toast.message(`Upload complete — ${dup} already uploaded (skipped)`);
    else toast.success("Upload complete");
    onDone?.();
  };

  return (
    <div>
      <div className="mb-5 rounded-xl border border-[#262626] bg-[#141414] p-4" data-testid="upload-library-selector">
        <label className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">Content Library</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {[{ v: "originals", l: "BareFootage Originals" }, { v: "celebrities", l: "Celebrities" }, { v: "pornstars", l: "Pornstars" }, { v: "web_finds", l: "Best of the Web" }].map((o) => (
            <button key={o.v} data-testid={`upload-lib-${o.v}`} onClick={() => setLibrary(o.v)}
              className={cn("rounded-full px-4 py-1.5 text-sm border transition-colors",
                library === o.v ? "bg-[#d9a24a] text-black border-[#d9a24a] font-medium" : "border-[#262626] text-[#c9c9c9] hover:border-[#d9a24a]/50")}>
              {o.l}
            </button>
          ))}
        </div>
        {needsName && (
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">{library === "pornstars" ? "Performer / Named Subject" : "Celebrity / Named Subject"} <span className="text-[#d9a24a]">*</span></label>
            <Input data-testid="upload-named-subject" value={named} onChange={(e) => setNamed(e.target.value)}
              placeholder={library === "pornstars" ? "e.g. Performer Name" : "e.g. Celebrity Name"}
              className="mt-1.5 bg-[#0f0f0f] border-[#262626] max-w-md" />
            <p className="text-[11px] text-[#8b8b8b] mt-1">You enter this manually — AI never identifies the person. Separate multiple names with commas.</p>
          </div>
        )}
        <div className="mt-3" data-testid="upload-subject-type-block">
          <label className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">Subject Type <span className="normal-case tracking-normal">(optional — your choice, never inferred)</span></label>
          <select data-testid="upload-subject-type" value={subjectType} onChange={(e) => setSubjectType(e.target.value)} className="mt-1.5 w-full bg-[#0f0f0f] border border-[#262626] rounded-md text-sm px-2 py-1.5">
            <option value="">Select subject type</option>
            <option value="candid">Candid</option>
            <option value="creator">Creator</option>
            <option value="celebrity">Celebrity</option>
            <option value="pornstar">Pornstar</option>
          </select>
          {isWeb && <p data-testid="upload-web-helper" className="mt-1.5 text-[11px] text-[#d9a24a]">Best of the Web tells us where the media came from. Subject Type describes the person or scene.</p>}
        </div>
        {isWeb && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="upload-web-source">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">Named Subject <span className="text-[#8b8b8b] normal-case tracking-normal">(optional)</span></label>
              <Input data-testid="upload-web-named-subject" value={named} onChange={(e) => setNamed(e.target.value)} placeholder="Only if you know who it is — comma-separate multiple" className="mt-1.5 bg-[#0f0f0f] border-[#262626]" />
            </div>
            {[["source_url", "Source URL", "https://…"], ["source_name", "Source site / name", "e.g. Reddit, X, Tumblr"], ["source_credit", "Creator / Credit", "e.g. @handle or photographer"], ["source_notes", "Notes", "anything worth remembering"]].map(([k, l, ph]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-[0.1em] text-[#8b8b8b] font-medium">{l} <span className="normal-case tracking-normal">(optional)</span></label>
                <Input data-testid={`upload-${k.replace("_", "-")}`} value={source[k]} onChange={(e) => setSource((s) => ({ ...s, [k]: e.target.value }))} placeholder={ph} className="mt-1.5 bg-[#0f0f0f] border-[#262626]" />
              </div>
            ))}
            <p className="md:col-span-2 text-[11px] text-[#8b8b8b]">Internet finds live in their own library — they never mix into Originals, Celebrities, Pornstars or Community. Every item lands in <span className="text-[#d9a24a]">Best of the Web → Random</span> and the normal tag galleries automatically.</p>
          </div>
        )}
      </div>
      <div data-testid="bulk-dropzone" onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }}
        className="rounded-xl border-2 border-dashed border-border hover:border-primary/50 p-12 text-center cursor-pointer transition-colors">
        <UploadCloud className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-sm">Drag & drop or <span className="text-primary font-medium">browse</span> — select multiple files</p>
        <p className="text-xs text-muted-foreground mt-1">Photos & videos (any length). Publishes instantly as Official.</p>
        <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid="bulk-input" onChange={(e) => add(e.target.files)} />
      </div>

      {queue.length > 0 && (
        <div className="mt-5 space-y-2">
          {(() => {
            const vids = queue.filter((q) => q.file.type.startsWith("video"));
            if (vids.length === 0) return null;
            const count = (k) => vids.filter((q) => q.pf && (k === "hfr" ? q.pf.hfr : q.pf.status === k)).length;
            const analyzing = vids.filter((q) => q.pf && q.pf.loading).length;
            return (
              <div className="rounded-lg border border-border bg-secondary/40 p-3 text-xs" data-testid="preflight-summary">
                <div className="font-heading text-sm mb-1.5">{vids.length} video{vids.length > 1 ? "s" : ""} selected{analyzing ? ` · analyzing ${analyzing}…` : ""}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                  {PREFLIGHT_BUCKETS.map((b) => <span key={b.key} data-testid={`preflight-count-${b.key}`}><span className="text-foreground font-medium">{count(b.key)}</span> {b.label}</span>)}
                </div>
              </div>
            );
          })()}
          {queue.map((q, i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
              <div className="w-10 h-10 rounded bg-secondary flex items-center justify-center shrink-0">
                {q.file.type.startsWith("video") ? <Video className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <input value={q.title} onChange={(e) => setQueue((qq) => qq.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))}
                  className="w-full bg-transparent border-b border-border/50 text-sm outline-none focus:border-primary py-1" />
                {q.pf && (
                  <div className="mt-1 text-[11px] flex flex-wrap items-center gap-x-2" data-testid={`preflight-${i}`}>
                    {q.pf.loading ? <span className="text-muted-foreground inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Inspecting…</span>
                      : <>
                          <span className={pfColor(q.pf.status)}>{q.pf.label}</span>
                          {q.pf.hfr && <span className="text-fuchsia-400">🎬 High frame rate — review slow motion</span>}
                          <span className="text-muted-foreground">
                            {q.pf.container}{q.pf.width ? ` · ${q.pf.width}×${q.pf.height}` : ""}{q.pf.duration ? ` · ${q.pf.duration.toFixed(1)}s` : ""}
                            {q.pf.fps ? ` · ${q.pf.fps}fps` : ""}{q.pf.acodec ? ` · ${q.pf.acodec}` : ""} · {(q.file.size / 1048576).toFixed(1)}MB
                          </span>
                        </>}
                  </div>
                )}
              </div>
              <span className="text-xs w-24 text-right shrink-0">
                {q.status === "done" ? <span className="text-emerald-400">✓ Done</span>
                  : q.status === "dup" ? <span className="text-amber-400">Duplicate</span>
                  : q.status === "uploading" ? <span className="text-primary inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" />{q.pct ? `${q.pct}%` : ""}</span>
                  : q.status === "error" ? <span className="text-destructive">Failed</span>
                  : <span className="text-muted-foreground">Queued</span>}
              </span>
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <Button data-testid="bulk-start" onClick={start} disabled={uploading} className="rounded-full">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</> : <>Publish {queue.filter((q) => q.status !== "done" && q.status !== "dup").length} item(s)</>}
            </Button>
            <Button variant="outline" onClick={() => setQueue([])} disabled={uploading} className="rounded-full border-border">Clear</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const Members = ({ onChange, onOpenProfile }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState(null); // null = all | contributor | viewer
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: -1 });
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/admin/users"); setUsers(r.data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleBan = async (u) => {
    const banning = !u.banned;
    if (banning && !window.confirm(`Boot ${u.name} off? This blocks their login and hides all their posts & comments (reversible — Reinstate restores them).`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/ban`, { banned: banning });
      toast.success(banning ? "User booted" : "User reinstated");
      load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleRole = (r) => setRoleFilter((cur) => (cur === r ? null : r));   // click active pill again -> All
  const sortBy = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : (key === "name" ? 1 : -1) }));
  const ql = q.trim().toLowerCase();
  const roleOf = (u) => ((u.role || "viewer").toLowerCase());   // accounts that never finished contributor onboarding are viewers
  const visible = users
    .filter((u) => !roleFilter || roleOf(u) === roleFilter)
    .filter((u) => !ql || (u.name || "").toLowerCase().includes(ql) || (u.email || "").toLowerCase().includes(ql))
    .sort((a, b) => {
      const av = a[sort.key] ?? "", bv = b[sort.key] ?? "";
      const c = typeof av === "number" || typeof bv === "number" ? (Number(av) - Number(bv)) : String(av).localeCompare(String(bv));
      return c * sort.dir;
    });
  const counts = users.reduce((m, u) => { const r = roleOf(u); m[r] = (m[r] || 0) + 1; return m; }, { contributor: 0, viewer: 0 });
  const Th = ({ k, children, className }) => (
    <th className={cn("text-left p-4", className)}>
      <button type="button" data-testid={`members-sort-${k}`} onClick={() => sortBy(k)} className={cn("inline-flex items-center gap-1 uppercase tracking-widest hover:text-foreground", sort.key === k && "text-primary")}>
        {children}{sort.key === k && <span aria-hidden="true">{sort.dir === 1 ? "↑" : "↓"}</span>}
      </button>
    </th>
  );

  if (loading) return <div className="h-48 flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="members-filters">
        <div className="inline-flex items-center gap-1.5" role="group" aria-label="Filter by role">
          {[["contributor", "Contributor"], ["viewer", "Viewer"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`members-role-${v}`} aria-pressed={roleFilter === v} onClick={() => toggleRole(v)}
              className={cn("px-3.5 py-1.5 rounded-full text-sm border transition-colors", roleFilter === v ? "bg-primary text-primary-foreground border-primary font-medium" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40")}>
              {l}{counts[v] != null && <span className={cn("ml-1.5 text-xs", roleFilter === v ? "text-primary-foreground/80" : "text-muted-foreground")}>{counts[v]}</span>}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground" data-testid="members-count">{roleFilter ? `${visible.length} ${roleFilter}${visible.length === 1 ? "" : "s"}` : `All users · ${visible.length}`}</span>
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input data-testid="members-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="bg-secondary/50 border-border h-9 pl-8" />
        </div>
      </div>
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-secondary/40 text-muted-foreground text-xs uppercase tracking-widest">
          <tr><Th k="name">Member</Th><Th k="role" className="hidden sm:table-cell">Role</Th><Th k="uploads" className="hidden md:table-cell">Uploads</Th><Th k="votes" className="hidden md:table-cell">Votes</Th><th className="text-right p-4">Action</th></tr>
        </thead>
        <tbody>
          {visible.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground" data-testid="members-empty">No {roleFilter ? `${roleFilter}s` : "users"}{ql ? ` matching “${q}”` : ""}.</td></tr>}
          {visible.map((u) => (
            <tr key={u.user_id} data-testid={`member-${u.user_id}`} className="border-t border-border">
              <td className="p-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-8 h-8 border border-border"><AvatarImage src={resolveAvatar(u.avatar_url)} /><AvatarFallback className="bg-secondary text-[10px]">{u.name?.[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2">{u.name}{u.is_admin && <span className="text-[9px] uppercase font-bold text-primary border border-primary/40 rounded px-1">Owner</span>}{u.banned && <span className="text-[9px] uppercase font-bold text-destructive border border-destructive/40 rounded px-1">Banned</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                </div>
              </td>
              <td className="p-4 hidden sm:table-cell capitalize text-muted-foreground" data-testid="member-role">{roleOf(u)}</td>
              <td className="p-4 hidden md:table-cell font-mono">{u.uploads}</td>
              <td className="p-4 hidden md:table-cell font-mono">{u.votes}</td>
              <td className="p-4 text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button size="icon" variant="outline" data-testid={`view-profile-${u.user_id}`} onClick={() => onOpenProfile?.(u.user_id)} className="h-8 w-8 rounded-full border-border"><Eye className="w-3.5 h-3.5" /></Button>
                  {u.is_admin ? <span className="text-xs text-muted-foreground">—</span>
                    : <Button size="sm" variant="outline" data-testid={`ban-${u.user_id}`} onClick={() => toggleBan(u)}
                        className={cn("rounded-full h-8 border-border", u.banned ? "hover:bg-emerald-500/10 hover:text-emerald-400" : "hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50")}>
                        {u.banned ? <><RotateCcw className="w-3.5 h-3.5 mr-1" />Reinstate</> : <><Ban className="w-3.5 h-3.5 mr-1" />Boot off</>}
                      </Button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
};

export default Admin;
